import { useCompanyBilling, BillingNotice } from '../components/CompanyBilling.jsx';
import { CompanyMetaSetup } from '../components/CompanyMetaSetup.jsx';
import { campaignMetaRenewalReturnStep, campaignMetaStepBlockedByRenewal, campaignMetaStepNavigationBlocked, campaignSetupBinding, rememberCompanyMetaSetupLaunch, setupReady } from '../company-meta-setup.js';
import { safeHttpsExternalUrl, validRecommendedInstantFormPreview } from '../recommended-form-preview.js';
export { validRecommendedInstantFormPreview } from '../recommended-form-preview.js';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { IconCheck, IconPlay } from '../components/icons.jsx';
import ProtectedVideo from '../components/ProtectedVideo.jsx';
import Beacon from '../components/Beacon.jsx';
import Select from '../components/Select.jsx';
import {
  CuratedMetaPublishProgress,
  initialCuratedMetaPublishProgress,
  normalizeCuratedMetaPublishProgress,
  useCuratedMetaPublishProgress,
  yieldCuratedMetaPublishProgressPaint,
} from '../components/CuratedMetaPublishProgress.jsx';
import {
  PausedCreationProgressModal,
  pausedCreationProgressModalCopy,
} from '../components/curated-campaign/PausedCreationProgressModal.jsx';
import {
  ConfirmationDialog,
  useConfirmationDialog,
} from '../components/ConfirmationDialog.jsx';
import { hasNamedTenantAction } from '../product-policy.js';
import { safeFailureMessage } from '../safe-failure.js';
import { ensureAndLoadCompanyBrands } from '../company-campaign-identity.js';
import {
  curatedMetaPublishConfirmation,
  curatedMetaPublishRecovery,
  curatedMetaPublishScopeKey,
  executeCuratedMetaPublish,
} from '../curated-meta-publish.js';
import {
  COLLAPSED_CREATIVE_COUNT,
  PosterThumbnail,
  QuickPlayDialog,
  useCuratedCreativePreview,
  usePreviewCloseFocus,
} from './CuratedHome.jsx';
import {
  catalogGroupsSignature,
  groupPublishedEntries,
  readTenantCatalogInBackground,
  useCuratedCatalogRefresh,
  validRenovationTypeKey,
} from '../curated-catalog.js';
import {
  captureTenantViewportAnchor,
  restoreTenantViewportAnchor,
  scrollCampaignStepIntoView,
} from '../shell/tenant-scroll.js';
import {
  CURATED_META_SYNC_RECOVERED_OUTCOME,
  CuratedMetaConnectionActions,
  createMetaAuthorizationLauncher,
  metaRenewalRequired,
  checkCuratedMetaAuthorization,
  curatedMetaAuthorizationPresentation,
  curatedMetaAuthorizationUsable,
  curatedMetaConnectionPresentation,
  curatedMetaConnectionState,
  curatedMetaResourceCatalog,
  curatedMetaRetryAfterSeconds,
  curatedMetaSyncQueued,
  curatedMetaSyncRecoveryNotice,
  metaCredentialId,
  refreshCuratedMetaConnection,
  startCuratedMetaOAuth,
  useCuratedMetaConnection,
  useSettledMetaAuthorizationState,
} from '../components/CuratedMetaConnection.jsx';
import {
  instantFormSetupOffersCreation,
  instantFormSetupUsesExistingForm,
  normalizeInstantFormSetup,
} from '../instant-form-setup.js';

const MIN_CREATIVES = 1;
const MAX_CREATIVES = 2;
const STEPS = Object.freeze([
  Object.freeze({ id: 'creative', label: 'Creative' }),
  Object.freeze({ id: 'meta', label: 'Meta setup' }),
  Object.freeze({ id: 'targeting', label: 'Targeting' }),
  Object.freeze({ id: 'budget', label: 'Budget' }),
  Object.freeze({ id: 'review', label: 'Review' }),
]);
const DEFAULT_BOUNDS = Object.freeze({
  CA: Object.freeze({ unit: 'km', min: 17, max: 80 }),
  US: Object.freeze({ unit: 'mi', min: 10, max: 50 }),
});
const PUBLISH_DETAIL_STATES = new Set(['paused', 'partial', 'failed']);
const MAX_TERMINAL_RECONCILIATION_ROUNDS = 2;
const PREFLIGHT_RECOVERY_REASON = 'curated_meta_budget_below_provider_minimum';
const PREFLIGHT_RECOVERY_ACTION = 'edit_and_reapprove';
const PREFLIGHT_RECOVERY_TOP_LEVEL_FIELDS = Object.freeze([
  'state',
  'canRecover',
  'action',
  'reasonCode',
  'failure',
]);
const PREFLIGHT_RECOVERY_FAILURE_FIELDS = Object.freeze([
  'code',
  'field',
  'minimumDailyMinor',
  'minimumMonthlyMajor',
  'currency',
  'zeroProviderObjectsCreated',
]);
const PREFLIGHT_BUDGET_MINIMUM_FIELDS = Object.freeze([
  'field',
  'minimumDailyMinor',
  'minimumMonthlyMajor',
  'currency',
]);
const PREFLIGHT_BUDGET_MINIMUM_UNAVAILABLE_FIELDS = Object.freeze([
  'state',
  'reasonCode',
]);
const INVALID_PREFLIGHT_RECOVERY = Object.freeze({
  state: 'invalid',
  canRecover: false,
  action: null,
  reasonCode: 'curated_preflight_recovery_response_invalid',
  failure: null,
});
const INVALID_PREFLIGHT_BUDGET_MINIMUM = Object.freeze({ state: 'invalid' });
const SAFE_META_RESOURCE_FIELDS = Object.freeze([
  'providerId',
  'eligibilityState',
  'readinessCode',
  'lastVerifiedAt',
  'currency',
  'preparable',
  'creatable',
  'selectableForVerification',
]);
const SAFE_META_PAGE_FIELDS = Object.freeze(['resourceId', 'providerId', 'label']);
const SAFE_RECOMMENDED_FORM_FIELDS = Object.freeze([
  'state',
  'code',
  'eligibleActiveFormCount',
]);
const useBrowserLayoutEffect = typeof document === 'undefined' ? useEffect : useLayoutEffect;

export function shouldOpenCuratedCampaignDetail(status) {
  return PUBLISH_DETAIL_STATES.has(stringValue(status).toLowerCase());
}

// A sealed intent that failed before the executor claimed it (OGC-432) has
// zero Meta objects and projects as `failed` at `preparing`. The final create
// click re-enters that same sealed intent, so it is offered again alongside
// the inline recovery notice; a failure with any durable object is not.
export function stalledPausedCreationProgress(progress) {
  const current = progress && typeof progress === 'object' ? progress : {};
  return current.state === 'failed'
    && current.stage === 'preparing'
    && current.retryable === true
    && Number(current.campaignCount) === 0
    && Number(current.adSetCount) === 0
    && Number(current.completedAds) === 0;
}

export function approvedCampaignPublishProgressAllowsCreate({
  status,
  hasRead,
  readUnavailable,
  progressState,
  progress = null,
} = {}) {
  return stringValue(status).toLowerCase() === 'approved'
    && hasRead === true
    && readUnavailable !== true
    && (progressState === 'not_started' || stalledPausedCreationProgress(progress));
}

// The modal (and the inline section) must show the staged "Preparing" view
// from the first click, even while the durable read still reports
// not_started on its provisional company:campaign scope.
export function pausedCreationModalProgress({ staging = false, progress, expectedAds } = {}) {
  const normalized = normalizeCuratedMetaPublishProgress(progress);
  if (normalized.state !== 'not_started') return normalized;
  return staging ? initialCuratedMetaPublishProgress(expectedAds) : normalized;
}

const PAUSED_CREATION_CONTROL_STATES = new Set(['draft', 'awaiting_approval', 'approved', 'partial', 'failed']);
const PAUSED_CREATION_RECOVERY_CODES = new Set([
  'meta_reauthorization_required',
  'paused_creation_meta_verification_required',
  'paused_creation_meta_verification_failed',
]);

// While the click's request is in flight the disabled "Creating in Meta…"
// control stays rendered whatever durable status a mid-flight read lands on
// (including publishing); otherwise the control follows the creation review.
// The in-flight relaxation only ever widens which status is acceptable — it
// never overrides the independent canCreate / reconciliation gates.
export function pausedCreationControlVisible({
  busy,
  status,
  canCreate,
  publishReconciliationRequired = false,
  publishProgressAllowsCreate = false,
} = {}) {
  const inFlight = stringValue(busy) === 'publish';
  const currentStatus = stringValue(status).toLowerCase();
  return canCreate !== false
    && publishReconciliationRequired !== true
    && (inFlight || currentStatus !== 'approved' || publishProgressAllowsCreate === true)
    && (inFlight || PAUSED_CREATION_CONTROL_STATES.has(currentStatus));
}

export function shouldReconcileCuratedMetaPublishTerminal({
  campaignId,
  busy,
  status,
  progressState,
} = {}) {
  return Boolean(stringValue(campaignId))
    && !stringValue(busy)
    && stringValue(status).toLowerCase() === 'publishing'
    && ['failed', 'succeeded'].includes(stringValue(progressState).toLowerCase());
}

export function advanceCuratedMetaTerminalReconciliation(previous, reconciliationKey) {
  const key = typeof reconciliationKey === 'string' ? reconciliationKey.trim() : '';
  const prior = previous && typeof previous === 'object' && !Array.isArray(previous)
    ? previous
    : {};
  const rounds = prior.key === key && Number.isSafeInteger(prior.rounds)
    ? prior.rounds
    : 0;
  if (!key || rounds >= MAX_TERMINAL_RECONCILIATION_ROUNDS) {
    return Object.freeze({ key, rounds, accepted: false, exhausted: Boolean(key) });
  }
  return Object.freeze({ key, rounds: rounds + 1, accepted: true, exhausted: false });
}

export async function loadDurableCuratedCampaignAfterTerminal({
  requestApi = api,
  companyId,
  campaignId,
  maxAttempts = 4,
  retryDelayMs = 300,
  wait = (delay) => new Promise((resolve) => setTimeout(resolve, delay)),
} = {}) {
  const expectedCompanyId = stringValue(companyId);
  const expectedCampaignId = stringValue(campaignId);
  const attempts = Math.max(1, Math.min(Number(maxAttempts) || 1, 5));
  const delay = Math.max(0, Math.min(Number(retryDelayMs) || 0, 2_000));
  if (!expectedCompanyId || !expectedCampaignId) {
    throw new Error('Campaign status is unavailable.');
  }
  let lastError = null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const response = await requestApi.get(
        `/api/saas/campaigns/${encodeURIComponent(expectedCampaignId)}/curated-configuration`,
        tenantHeaders(expectedCompanyId),
      );
      if (
        !response
        || typeof response !== 'object'
        || Array.isArray(response)
        || response.campaign?.id !== expectedCampaignId
        || !stringValue(response.campaign?.status)
      ) throw new Error('Campaign status is unavailable.');
      if (campaignStatus(response) !== 'publishing' || attempt === attempts - 1) {
        return response;
      }
    } catch (error) {
      lastError = error;
      if (attempt === attempts - 1) throw error;
    }
    await wait(delay);
  }
  throw lastError || new Error('Campaign status is unavailable.');
}

function tenantHeaders(companyId) {
  return { headers: { 'x-company-id': companyId } };
}

function stringValue(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function finiteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function monthlyMajorMinor(value) {
  const text = String(value ?? '').trim();
  if (text.length < 1 || text.length > 32 || !/^\d+(?:\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  return (BigInt(whole) * 100n) + BigInt(fraction.padEnd(2, '0'));
}

function validMonthlyMajor(value) {
  const minor = monthlyMajorMinor(value);
  return minor !== null && minor > 0n;
}

function hasExactObjectFields(value, fields) {
  return Boolean(
    value
    && typeof value === 'object'
    && !Array.isArray(value)
    && Object.keys(value).length === fields.length
    && fields.every((field) => Object.hasOwn(value, field)),
  );
}

export function preflightRecoveryView(response) {
  if (!response || typeof response !== 'object') return null;
  if (!Object.hasOwn(response, 'preflightRecovery')) return INVALID_PREFLIGHT_RECOVERY;
  const source = response.preflightRecovery;
  if (source === null) return null;
  if (!hasExactObjectFields(source, PREFLIGHT_RECOVERY_TOP_LEVEL_FIELDS)) {
    return INVALID_PREFLIGHT_RECOVERY;
  }
  if (source.state === 'check_unavailable') {
    if (
      source.canRecover !== false
      || source.action !== null
      || source.reasonCode !== 'curated_preflight_recovery_check_unavailable'
      || source.failure !== null
    ) return INVALID_PREFLIGHT_RECOVERY;
    return Object.freeze({
      state: 'check_unavailable',
      canRecover: false,
      action: null,
      reasonCode: source.reasonCode,
      failure: null,
    });
  }
  if (!['available', 'manager_required'].includes(source.state)) {
    return INVALID_PREFLIGHT_RECOVERY;
  }
  if (!hasExactObjectFields(source.failure, PREFLIGHT_RECOVERY_FAILURE_FIELDS)) {
    return INVALID_PREFLIGHT_RECOVERY;
  }
  const currency = stringValue(source.failure.currency);
  const minimumDailyMinor = source.failure.minimumDailyMinor;
  const minimumMonthlyMajor = stringValue(source.failure.minimumMonthlyMajor);
  const available = source.state === 'available';
  if (
    source.reasonCode !== PREFLIGHT_RECOVERY_REASON
    || source.failure.code !== PREFLIGHT_RECOVERY_REASON
    || source.failure.field !== 'budget.monthlyMajor'
    || source.failure.zeroProviderObjectsCreated !== true
    || !Number.isSafeInteger(minimumDailyMinor)
    || minimumDailyMinor <= 0
    || minimumDailyMinor > 1_000_000_000
    || !validMonthlyMajor(minimumMonthlyMajor)
    || !/^[A-Z]{3}$/.test(currency)
    || source.canRecover !== available
    || source.action !== (available ? PREFLIGHT_RECOVERY_ACTION : null)
  ) return INVALID_PREFLIGHT_RECOVERY;
  return Object.freeze({
    state: source.state,
    canRecover: available,
    action: available ? PREFLIGHT_RECOVERY_ACTION : null,
    reasonCode: PREFLIGHT_RECOVERY_REASON,
    failure: Object.freeze({
      code: PREFLIGHT_RECOVERY_REASON,
      field: 'budget.monthlyMajor',
      minimumDailyMinor,
      minimumMonthlyMajor: normalizedMonthlyMajor(minimumMonthlyMajor),
      currency,
      zeroProviderObjectsCreated: true,
    }),
  });
}

export function preflightRecoveryBlocksMetaCreation(recovery) {
  return recovery !== null;
}

export function preflightBudgetMinimumView(response) {
  if (!response || typeof response !== 'object') return null;
  if (!Object.hasOwn(response, 'preflightBudgetMinimum')) {
    return INVALID_PREFLIGHT_BUDGET_MINIMUM;
  }
  const source = response.preflightBudgetMinimum;
  if (source === null) return null;
  if (hasExactObjectFields(source, PREFLIGHT_BUDGET_MINIMUM_UNAVAILABLE_FIELDS)) {
    if (
      source.state === 'check_unavailable'
      && source.reasonCode === 'curated_preflight_recovery_check_unavailable'
    ) {
      return Object.freeze({
        state: 'check_unavailable',
        reasonCode: source.reasonCode,
      });
    }
    return INVALID_PREFLIGHT_BUDGET_MINIMUM;
  }
  if (!hasExactObjectFields(source, PREFLIGHT_BUDGET_MINIMUM_FIELDS)) {
    return INVALID_PREFLIGHT_BUDGET_MINIMUM;
  }
  const currency = stringValue(source.currency);
  const minimumMonthlyMajor = stringValue(source.minimumMonthlyMajor);
  if (
    source.field !== 'budget.monthlyMajor'
    || !Number.isSafeInteger(source.minimumDailyMinor)
    || source.minimumDailyMinor <= 0
    || source.minimumDailyMinor > 1_000_000_000
    || !validMonthlyMajor(minimumMonthlyMajor)
    || !/^[A-Z]{3}$/.test(currency)
  ) return INVALID_PREFLIGHT_BUDGET_MINIMUM;
  return Object.freeze({
    state: 'available',
    field: 'budget.monthlyMajor',
    minimumDailyMinor: source.minimumDailyMinor,
    minimumMonthlyMajor: normalizedMonthlyMajor(minimumMonthlyMajor),
    currency,
  });
}

export function canEditAndReapprovePreflight(recovery, canApprove) {
  return Boolean(
    canApprove
    && recovery?.state === 'available'
    && recovery.canRecover === true
    && recovery.action === PREFLIGHT_RECOVERY_ACTION
    && recovery.failure?.zeroProviderObjectsCreated === true,
  );
}

function currentPublishedEntry(entry) {
  const typeKey = stringValue(entry?.renovationType?.key);
  const typeId = stringValue(entry?.renovationType?.id);
  const packageRow = entry?.package || {};
  const version = entry?.version || {};
  if (
    !validRenovationTypeKey(typeKey)
    || !typeId
    || !stringValue(version.id)
    || packageRow.lifecycle !== 'published'
    || version.status !== 'published'
    || (packageRow.publishedVersionId && packageRow.publishedVersionId !== version.id)
    || typeof version.primaryText !== 'string'
    || typeof version.headline !== 'string'
  ) return null;
  return entry;
}

// OGC-531: types, names and order come from the authorized catalog. Every
// eligible Published creative is offered; selection stays limited to 1-2.
export function normalizeCuratedCatalog(value) {
  const entries = (Array.isArray(value) ? value : []).map(currentPublishedEntry).filter(Boolean);
  return groupPublishedEntries(entries).map((group) => Object.freeze({
    ...group,
    choices: Object.freeze(group.choices),
    available: group.choices.length >= MIN_CREATIVES,
    shortfall: Math.max(0, MIN_CREATIVES - group.choices.length),
  }));
}

// A catalog-only refresh: options other than the catalog are left untouched,
// and an unchanged customer-visible catalog keeps the current view.
export function applyCuratedCatalogRefresh(current, { companyId, scopeKey, catalog } = {}) {
  if (
    !current
    || current.companyId !== companyId
    || current.scopeKey !== scopeKey
    || current.status !== 'ready'
    || !current.options
  ) return current;
  const nextCatalog = normalizeCuratedCatalog(catalog);
  if (catalogGroupsSignature(nextCatalog) === catalogGroupsSignature(current.options.catalog)) return current;
  return {
    ...current,
    catalogEpoch: Math.max(0, Number(current.catalogEpoch) || 0) + 1,
    options: Object.freeze({ ...current.options, catalog: Object.freeze(nextCatalog) }),
  };
}

// OGC-531: a selection is a (renovation type, creative version) pair, so a
// creative the operator moves to another type is never selected there by
// accident. Stale selections are derived from the live catalog rather than
// stored: a saved pair that is no longer offered under its type stays in the
// draft, blocks Continue until it is replaced, and clears if the creative is
// offered again. Nothing is replaced silently.
function selectionPairKey(renovationTypeId, creativeVersionId) {
  return `${renovationTypeId}:${creativeVersionId}`;
}

function uniqueSelectionPairs(selections) {
  const seen = new Set();
  return (Array.isArray(selections) ? selections : []).filter((selection) => {
    const key = selectionPairKey(selection?.renovationTypeId, selection?.creativeVersionId);
    if (!selection?.renovationTypeId || !selection?.creativeVersionId || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function curatedChoiceSelected(draft, renovationTypeId, creativeVersionId) {
  return (draft?.selections || []).some((selection) => (
    selection.renovationTypeId === renovationTypeId && selection.creativeVersionId === creativeVersionId
  ));
}

export function curatedSelectedChoices(draft, group) {
  return group.choices.filter((choice) => curatedChoiceSelected(draft, group.id, choice.version.id));
}

export function curatedStaleSelections(options, draft) {
  const offered = new Set((options?.catalog || []).flatMap((group) => (
    group.choices.map((choice) => selectionPairKey(group.id, choice.version.id))
  )));
  return (draft?.selections || []).filter((selection) => (
    draft.selectedTypeIds.includes(selection.renovationTypeId)
    && !offered.has(selectionPairKey(selection.renovationTypeId, selection.creativeVersionId))
  ));
}

function normalizedResource(resource) {
  const resourceId = stringValue(resource?.resourceId || resource?.id);
  if (!resourceId) return null;
  const normalized = {
    resourceId,
    label: stringValue(resource.label) || 'Meta resource',
    selectable: resource.selectable !== false,
  };
  for (const field of SAFE_META_RESOURCE_FIELDS) {
    if (Object.hasOwn(resource, field)) normalized[field] = resource[field];
  }
  if (resource.owningPage && typeof resource.owningPage === 'object') {
    normalized.owningPage = Object.freeze(Object.fromEntries(
      SAFE_META_PAGE_FIELDS
        .filter((field) => Object.hasOwn(resource.owningPage, field))
        .map((field) => [field, resource.owningPage[field]]),
    ));
  }
  if (resource.recommendedFormCreation && typeof resource.recommendedFormCreation === 'object') {
    normalized.recommendedFormCreation = Object.freeze(Object.fromEntries(
      SAFE_RECOMMENDED_FORM_FIELDS
        .filter((field) => Object.hasOwn(resource.recommendedFormCreation, field))
        .map((field) => [field, resource.recommendedFormCreation[field]]),
    ));
  }
  return Object.freeze(normalized);
}

function normalizeMetaConnections(value) {
  const connections = Array.isArray(value) ? value : [];
  return connections.map((connection, index) => Object.freeze({
    credentialId: stringValue(connection?.credentialId || connection?.id) || `connection-${index + 1}`,
    connectionState: stringValue(connection?.connectionState || connection?.status) || 'unknown',
    reconciliationStatus: stringValue(connection?.reconciliationStatus),
    readinessCode: stringValue(connection?.readinessCode),
    appSubscriptionState: stringValue(connection?.appSubscriptionState),
    catalogFreshness: stringValue(connection?.catalogFreshness),
    usesLastKnownGood: connection?.usesLastKnownGood === true,
    stale: connection?.stale === true,
    refreshRequired: connection?.refreshRequired === true,
    refreshInProgress: connection?.refreshInProgress === true,
    retryAfterSeconds: Number.isSafeInteger(connection?.retryAfterSeconds)
      ? Math.min(Math.max(connection.retryAfterSeconds, 1), 30)
      : null,
    lastVerifiedAt: stringValue(connection?.lastVerifiedAt),
    cacheExpiresAt: stringValue(connection?.cacheExpiresAt),
    adAccounts: Object.freeze((Array.isArray(connection?.adAccounts) ? connection.adAccounts : [])
      .map(normalizedResource).filter(Boolean)),
    pages: Object.freeze((Array.isArray(connection?.pages) ? connection.pages : [])
      .map(normalizedResource).filter(Boolean)
      .map((page) => Object.freeze({ ...page, selectable: false }))),
    instantForms: Object.freeze((Array.isArray(connection?.instantForms) ? connection.instantForms : [])
      .map(normalizedResource).filter(Boolean)),
    instagramIdentities: Object.freeze((Array.isArray(connection?.instagramIdentities) ? connection.instagramIdentities : [])
      .map(normalizedResource).filter(Boolean)),
  }));
}

function normalizeLocations(value, country) {
  const seen = new Set();
  const result = [];
  for (const rawLocation of Array.isArray(value) ? value : []) {
    const key = stringValue(rawLocation?.key);
    const label = stringValue(rawLocation?.label);
    const countryCode = stringValue(rawLocation?.countryCode).toUpperCase();
    if (!key || !label || countryCode !== country || seen.has(key)) continue;
    seen.add(key);
    result.push(Object.freeze({ key, label, countryCode }));
  }
  return Object.freeze(result.sort((left, right) => left.label.localeCompare(right.label)));
}

function normalizeBounds(value, country) {
  const expectedUnit = DEFAULT_BOUNDS[country].unit;
  const unit = stringValue(value?.unit).toLowerCase() === expectedUnit
    ? expectedUnit
    : DEFAULT_BOUNDS[country].unit;
  const min = finiteNumber(value?.min, DEFAULT_BOUNDS[country].min);
  const max = finiteNumber(value?.max, DEFAULT_BOUNDS[country].max);
  return Object.freeze({
    unit,
    min: min > 0 ? min : DEFAULT_BOUNDS[country].min,
    max: max >= min ? max : DEFAULT_BOUNDS[country].max,
  });
}

export function normalizeCuratedOptions(value = {}) {
  return Object.freeze({
    catalog: Object.freeze(normalizeCuratedCatalog(value.catalog)),
    metaConnections: Object.freeze(normalizeMetaConnections(
      value.metaConnections || value.connections,
    )),
    locations: Object.freeze({
      CA: normalizeLocations(value.locations?.CA, 'CA'),
      US: normalizeLocations(value.locations?.US, 'US'),
    }),
    radiusBounds: Object.freeze({
      CA: normalizeBounds(value.radiusBounds?.CA, 'CA'),
      US: normalizeBounds(value.radiusBounds?.US, 'US'),
    }),
    budgetPolicy: (value.budgetPolicy || value.budgetRule) && typeof (value.budgetPolicy || value.budgetRule) === 'object'
      ? Object.freeze({ ...(value.budgetPolicy || value.budgetRule) })
      : null,
    instantFormSetup: normalizeInstantFormSetup(value.instantFormSetup),
  });
}

export function applyCuratedOptionsRefresh(current, {
  companyId,
  scopeKey,
  optionResponse,
} = {}) {
  if (
    !current
    || current.companyId !== companyId
    || current.scopeKey !== scopeKey
    || current.status !== 'ready'
  ) return current;
  return {
    ...current,
    catalogEpoch: Math.max(0, Number(current.catalogEpoch) || 0) + 1,
    options: normalizeCuratedOptions(optionResponse),
  };
}

export function shouldReloadCuratedOptionsAfterMetaSync(previous = {}, current = {}) {
  return previous.scopeKey === current.scopeKey
    && current.state === 'succeeded'
    && Number.isSafeInteger(current.generation)
    && Number.isSafeInteger(previous.generation)
    && (
      current.generation > previous.generation
      || (
        ['queued', 'running'].includes(previous.state)
        && current.generation >= previous.generation
      )
    );
}

// The connection hook resets its payload to empty while it loads or reloads,
// so a '' sync state is usually "not observed yet", not "never synced". Only a
// ready payload is an observation; the first one for a scope is the baseline
// the next real transition is measured against, never a completed sync in
// itself (OGC-426).
export function observeCuratedMetaSync(previous = {}, observation = {}) {
  if (observation.status !== 'ready') return { next: previous, reload: false };
  const next = {
    scopeKey: observation.scopeKey,
    state: observation.state,
    generation: observation.generation,
  };
  return { next, reload: shouldReloadCuratedOptionsAfterMetaSync(previous, next) };
}

export function feedbackAfterDraftEdit(feedback) {
  return feedback?.preserveOnDraftEdit === true ? feedback : null;
}

export function reconcileRecommendedFormUiState(current = {}, options = {}, accountSelection = {}) {
  const setup = options.instantFormSetup || normalizeInstantFormSetup();
  if (!instantFormSetupOffersCreation(setup)) {
    return Object.freeze({
      ...current,
      formMode: 'existing',
      pageResourceId: '',
      recommendedForm: null,
      conflict: null,
    });
  }

  const candidates = recommendedInstantFormCandidates(options, accountSelection);
  const currentPageResourceId = stringValue(current.pageResourceId);
  const currentCandidate = candidates.find((candidate) => (
    candidate.page.resourceId === currentPageResourceId
  ));
  const exactCandidate = candidates.length === 1 ? candidates[0] : currentCandidate;
  const pageResourceId = exactCandidate?.page.resourceId || '';
  const preservePageState = Boolean(pageResourceId && pageResourceId === currentPageResourceId);
  return Object.freeze({
    ...current,
    formMode: 'create',
    pageResourceId,
    recommendedForm: preservePageState ? current.recommendedForm || null : null,
    conflict: preservePageState ? current.conflict || null : null,
  });
}

export async function refreshCampaignMetaOptions({
  companyId,
  credentialId,
  refreshConnection = refreshCuratedMetaConnection,
  loadOptions = () => api.get(
    '/api/saas/curated-campaign-options',
    tenantHeaders(companyId),
  ),
  isCurrent = () => true,
  onRefreshCommitted = () => {},
} = {}) {
  const sync = await refreshConnection({ companyId, credentialId });
  if (!isCurrent()) return { outcome: 'stale' };
  try {
    onRefreshCommitted();
  } catch {
    // A local projection reload cannot change the already committed refresh.
  }

  // OGC-417. A recovered lineage is queued work, not a finished catalog. Left
  // out of this set it would fall through to the options reload below and be
  // reported as fully reconciled -- telling the marketer to choose resources
  // that the just-started sync has not produced yet.
  if (curatedMetaSyncQueued(sync)) {
    return {
      outcome: sync.outcome,
      syncState: sync.syncState,
      generation: sync.generation,
      ...(sync.outcome === CURATED_META_SYNC_RECOVERED_OUTCOME ? {
        recoveriesUsed: sync.recoveriesUsed,
        recoveriesRemaining: sync.recoveriesRemaining,
      } : {}),
    };
  }

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const optionResponse = await loadOptions();
      return isCurrent()
        ? { outcome: 'fully_reconciled', optionResponse, recoveredRead: attempt > 0 }
        : { outcome: 'stale' };
    } catch {
      // One bounded retry handles a transient projection read after the
      // committed refresh without turning it into a false mutation failure.
    }
  }
  return { outcome: 'options_reload_failed' };
}

function configurationSelections(configuration) {
  return (Array.isArray(configuration?.selections) ? configuration.selections : [])
    .map((selection) => ({
      renovationTypeId: stringValue(selection?.renovationTypeId),
      creativeVersionId: stringValue(selection?.creativeVersionId),
    }))
    .filter((selection) => selection.renovationTypeId && selection.creativeVersionId);
}

function configurationSelectedTypeIds(configuration) {
  const value = configuration?.selectedRenovationTypeIds;
  return Array.isArray(value)
    ? [...new Set(value.map(stringValue).filter(Boolean))]
    : null;
}

function selectedTypeIdsFromQuery(options, selectedTypes) {
  const keys = new Set(Array.isArray(selectedTypes) ? selectedTypes : []);
  return options.catalog.filter((group) => keys.has(group.key) && group.id).map((group) => group.id);
}

export function createCuratedDraft(options, selectedTypes = [], response = null) {
  const configuration = response?.configuration || {};
  const savedSelections = configurationSelections(configuration);
  const hasPersistedConfiguration = finiteNumber(configuration.revision, 0) > 0;
  const explicitlySelectedTypeIds = hasPersistedConfiguration
    ? configurationSelectedTypeIds(configuration)
    : null;
  const selectedTypeIds = explicitlySelectedTypeIds
    ?? (savedSelections.length
      ? [...new Set(savedSelections.map(({ renovationTypeId }) => renovationTypeId))]
      : selectedTypeIdsFromQuery(options, selectedTypes));
  const selections = hasPersistedConfiguration
    ? savedSelections
    : options.catalog
      .filter((group) => selectedTypeIds.includes(group.id) && group.choices.length >= MIN_CREATIVES && group.choices.length <= MAX_CREATIVES)
      .flatMap((group) => group.choices.map((choice) => ({ renovationTypeId: group.id, creativeVersionId: choice.version.id })));
  const country = ['CA', 'US'].includes(configuration?.targeting?.country)
    ? configuration.targeting.country
    : 'CA';
  const bounds = options.radiusBounds[country];
  const rawRadius = configuration?.targeting?.radius || {};
  const meta = configuration?.meta || {};
  const budget = configuration?.budget || {};
  return {
    selectedTypeIds,
    selections: uniqueSelectionPairs(selections),
    meta: {
      adAccountResourceId: stringValue(meta.adAccountResourceId),
      instantFormResourceId: stringValue(meta.instantFormResourceId),
      instagramIdentityResourceId: stringValue(meta.instagramIdentityResourceId),
    },
    targeting: {
      country,
      includedCityKeys: [...new Set(Array.isArray(configuration?.targeting?.includedCityKeys)
        ? configuration.targeting.includedCityKeys.map(stringValue).filter(Boolean)
        : [])],
      excludedCityKeys: [...new Set(Array.isArray(configuration?.targeting?.excludedCityKeys)
        ? configuration.targeting.excludedCityKeys.map(stringValue).filter(Boolean)
        : [])],
      radius: {
        value: rawRadius.value === undefined || rawRadius.value === null
          ? bounds.min
          : finiteNumber(rawRadius.value, bounds.min),
        unit: bounds.unit,
      },
    },
    budget: {
      monthlyMajor: budget.monthlyMajor === undefined || budget.monthlyMajor === null
        ? ''
        : String(budget.monthlyMajor),
    },
  };
}

function selectedAccount(options, draft) {
  for (const connection of options.metaConnections) {
    const account = connection.adAccounts.find((resource) => (
      resource.resourceId === draft.meta.adAccountResourceId
    ));
    if (account) return { connection, account };
  }
  return { connection: null, account: null };
}

function selectedForm(options, draft) {
  for (const connection of options.metaConnections) {
    const form = connection.instantForms.find((resource) => (
      resource.resourceId === draft.meta.instantFormResourceId
    ));
    if (form) return { connection, form };
  }
  return { connection: null, form: null };
}

function sameOwningPage(left, right) {
  if (!left || !right) return false;
  const leftId = stringValue(left.resourceId || left.providerId);
  const rightId = stringValue(right.resourceId || right.providerId);
  return Boolean(leftId && rightId && leftId === rightId);
}

function recommendedInstantFormCandidateForBinding(options, binding, mode) {
  const credentialId = stringValue(binding?.credentialId);
  const pageResourceId = stringValue(binding?.pageResourceId);
  if (!credentialId || !pageResourceId) return null;
  const connections = options.metaConnections.filter((connection) => (
    connection.credentialId === credentialId
  ));
  if (connections.length !== 1) return null;
  const pages = connections[0].pages.filter((page) => page.resourceId === pageResourceId);
  if (pages.length !== 1) return null;
  return Object.freeze({
    connection: connections[0],
    page: pages[0],
    mode,
    ...(mode === 'recover' ? { operationId: stringValue(binding.operationId) } : {}),
  });
}

export function recommendedInstantFormCandidates(options, accountSelection = {}) {
  const setup = options?.instantFormSetup || normalizeInstantFormSetup();
  if (setup.state === 'recovery_available') {
    const recovery = recommendedInstantFormCandidateForBinding(options, setup.recovery, 'recover');
    return Object.freeze(recovery ? [recovery] : []);
  }
  if (setup.state !== 'creation_available') return Object.freeze([]);

  const selectedCredentialId = stringValue(accountSelection?.connection?.credentialId);
  const selectedAccountResourceId = stringValue(accountSelection?.account?.resourceId);
  if (
    !selectedCredentialId
    || !selectedAccountResourceId
    || !accountSelection.connection.adAccounts.some((account) => (
      account.resourceId === selectedAccountResourceId && account.selectable
    ))
  ) return Object.freeze([]);

  return Object.freeze(setup.creationPages
    .filter((binding) => binding.credentialId === selectedCredentialId)
    .map((binding) => recommendedInstantFormCandidateForBinding(options, binding, 'create'))
    .filter(Boolean));
}

export function findRecommendedInstantFormCandidate(
  options,
  accountSelection,
  pageResourceId,
) {
  const selectedPageResourceId = stringValue(pageResourceId);
  if (!selectedPageResourceId) return null;
  const matches = recommendedInstantFormCandidates(options, accountSelection)
    .filter((candidate) => candidate.page.resourceId === selectedPageResourceId);
  return matches.length === 1 ? matches[0] : null;
}

export function curatedDraftValidation(options, draft, {
  canEditBudget = true,
  allowServerBudget = false,
  minimumMonthlyMajor = '',
  minimumCurrency = '',
  minimumUnavailable = false,
  preserveRecordedMeta = false,
} = {}) {
  const findings = [];
  const add = (step, field, message) => findings.push(Object.freeze({ step, field, message }));
  if (!draft.selectedTypeIds.length) {
    add('creative', 'renovation-types', 'Select at least one renovation type.');
  }
  const staleSelections = curatedStaleSelections(options, draft);
  for (const typeId of draft.selectedTypeIds) {
    const group = options.catalog.find((candidate) => candidate.id === typeId);
    const selected = group ? curatedSelectedChoices(draft, group) : [];
    const stale = staleSelections.some((selection) => selection.renovationTypeId === typeId);
    if (!group || !group.available) {
      add('creative', `type-${typeId}`, 'This renovation type no longer has a Published choice.');
    } else if (stale) {
      add('creative', `type-${typeId}`, `A saved ${group.displayName} ad is no longer Published. Reselect one or two current ads.`);
    } else if (selected.length < MIN_CREATIVES || selected.length > MAX_CREATIVES) {
      add('creative', `type-${typeId}`, selected.length < MIN_CREATIVES ? `Choose at least 1 Published ad for ${group.displayName}.` : `Choose at most 2 Published ads for ${group.displayName}.`);
    }
  }
  if (!draft.meta.adAccountResourceId) {
    add('meta', 'meta-ad-account', 'Select a Meta ad account.');
  }
  if (!draft.meta.instantFormResourceId) {
    add('meta', 'meta-instant-form', 'Select a Meta Instant Form.');
  }
  const accountSelection = selectedAccount(options, draft);
  const formSelection = selectedForm(options, draft);
  const instantFormSetup = options.instantFormSetup || normalizeInstantFormSetup();
  const policyFormResourceIds = new Set(instantFormSetup.formResourceIds);
  const compatibleCredentialIds = new Set(instantFormSetup.compatibleCredentialIds);
  if (draft.meta.adAccountResourceId && (!accountSelection.account?.selectable || !accountSelection.connection)) {
    add('meta', 'meta-ad-account', 'The selected Meta ad account is not currently available.');
  } else if (
    draft.meta.adAccountResourceId
    && !preserveRecordedMeta
    && instantFormSetupUsesExistingForm(instantFormSetup)
    && !compatibleCredentialIds.has(accountSelection.connection?.credentialId)
  ) {
    add('meta', 'meta-ad-account', 'Choose an ad account from a Meta connection with a company Instant Form.');
  }
  if (draft.meta.instantFormResourceId && (
    (!formSelection.form?.selectable && !formSelection.form?.selectableForVerification)
    || !formSelection.connection
    || (!preserveRecordedMeta && !policyFormResourceIds.has(formSelection.form?.resourceId))
    || (!preserveRecordedMeta && !compatibleCredentialIds.has(formSelection.connection?.credentialId))
  )) {
    add('meta', 'meta-instant-form', 'The selected Instant Form is not currently available.');
  }
  if (
    accountSelection.connection
    && formSelection.connection
    && accountSelection.connection.credentialId !== formSelection.connection.credentialId
  ) {
    add('meta', 'meta-instant-form', 'Choose an Instant Form from the same Meta connection as the ad account.');
  }
  if (draft.meta.instagramIdentityResourceId) {
    const instagram = formSelection.connection?.instagramIdentities.find((identity) => (
      identity.resourceId === draft.meta.instagramIdentityResourceId
    ));
    if (
      (!instagram?.selectable && !instagram?.selectableForVerification)
      || !sameOwningPage(instagram.owningPage, formSelection.form?.owningPage)
    ) {
      add('meta', 'meta-instagram', 'Choose an available Instagram identity owned by the Form’s Facebook Page.');
    }
  }
  const countryLocations = new Set(options.locations[draft.targeting.country].map(({ key }) => key));
  if (!draft.targeting.includedCityKeys.length) {
    add('targeting', 'included-city-search', 'Add at least one included city.');
  }
  if (draft.targeting.includedCityKeys.some((key) => !countryLocations.has(key))) {
    add('targeting', 'included-city-search', 'Reselect included cities that are no longer available.');
  }
  if (draft.targeting.excludedCityKeys.some((key) => !countryLocations.has(key))) {
    add('targeting', 'excluded-city-search', 'Reselect excluded cities that are no longer available.');
  }
  if (draft.targeting.includedCityKeys.some((key) => draft.targeting.excludedCityKeys.includes(key))) {
    add('targeting', 'excluded-city-search', 'A city cannot be both included and excluded.');
  }
  const bounds = options.radiusBounds[draft.targeting.country];
  const radius = finiteNumber(draft.targeting.radius.value, NaN);
  if (!Number.isSafeInteger(radius) || radius < bounds.min || radius > bounds.max) {
    add('targeting', 'target-radius', `Enter a radius from ${bounds.min} to ${bounds.max} ${bounds.unit}.`);
  }
  const hasProviderMinimum = validMonthlyMajor(minimumMonthlyMajor);
  if (minimumUnavailable) {
    add('budget', 'monthly-budget', 'Refresh this campaign before submitting because the Meta account minimum could not be verified.');
  } else if (hasProviderMinimum && !validMonthlyMajor(draft.budget.monthlyMajor)) {
    add(
      'budget',
      'monthly-budget',
      canEditBudget
        ? 'Enter a monthly campaign budget greater than zero with no more than two decimal places.'
        : 'A company owner or admin must correct the campaign budget before submission.',
    );
  } else if (
    hasProviderMinimum
    && monthlyMajorMinor(draft.budget.monthlyMajor) < monthlyMajorMinor(minimumMonthlyMajor)
  ) {
    add(
      'budget',
      'monthly-budget',
      canEditBudget
        ? `Enter at least ${displayCurrency(minimumMonthlyMajor, minimumCurrency)} monthly to meet the Meta ad account minimum.`
        : `A company owner or admin must raise the monthly budget to at least ${displayCurrency(minimumMonthlyMajor, minimumCurrency)}.`,
    );
  } else if (canEditBudget && !validMonthlyMajor(draft.budget.monthlyMajor)) {
    add('budget', 'monthly-budget', 'Enter a monthly campaign budget greater than zero with no more than two decimal places.');
  }
  if (
    !minimumUnavailable
    && !hasProviderMinimum
    && !canEditBudget
    && !allowServerBudget
    && !validMonthlyMajor(draft.budget.monthlyMajor)
  ) {
    add('budget', 'monthly-budget', 'A company owner or admin must set the campaign budget.');
  }
  return Object.freeze(findings);
}

export function curatedSelectedAdCount(options, draft) {
  if (!draft) return 0;
  return new Set(options.catalog
    .filter((group) => draft.selectedTypeIds.includes(group.id))
    .flatMap((group) => curatedSelectedChoices(draft, group).map((choice) => choice.version.id))).size;
}

export function buildCuratedConfigurationPayload(options, draft, expectedRevision) {
  const selections = [];
  const seenSelectionIds = new Set();
  for (const group of options.catalog) {
    if (!draft.selectedTypeIds.includes(group.id)) continue;
    for (const choice of curatedSelectedChoices(draft, group)) {
      if (seenSelectionIds.has(choice.version.id)) continue;
      selections.push({
        renovationTypeId: group.id,
        creativeVersionId: choice.version.id,
      });
      seenSelectionIds.add(choice.version.id);
    }
  }
  for (const selection of curatedStaleSelections(options, draft)) {
    if (!seenSelectionIds.has(selection.creativeVersionId)) {
      selections.push({
        renovationTypeId: selection.renovationTypeId,
        creativeVersionId: selection.creativeVersionId,
      });
      seenSelectionIds.add(selection.creativeVersionId);
    }
  }
  const rawRadiusValue = draft.targeting.radius.value;
  const radiusValue = rawRadiusValue === undefined
    || rawRadiusValue === null
    || (typeof rawRadiusValue === 'string' && rawRadiusValue.trim() === '')
    ? ''
    : finiteNumber(rawRadiusValue, 0);
  return {
    expectedRevision: finiteNumber(expectedRevision, 0),
    selectedRenovationTypeIds: [...draft.selectedTypeIds],
    selections,
    meta: {
      adAccountResourceId: draft.meta.adAccountResourceId,
      instantFormResourceId: draft.meta.instantFormResourceId,
      ...(draft.meta.instagramIdentityResourceId
        ? { instagramIdentityResourceId: draft.meta.instagramIdentityResourceId }
        : {}),
    },
    targeting: {
      country: draft.targeting.country,
      includedCityKeys: [...draft.targeting.includedCityKeys],
      excludedCityKeys: [...draft.targeting.excludedCityKeys],
      radius: {
        value: radiusValue,
        unit: options.radiusBounds[draft.targeting.country].unit,
      },
    },
    budget: {
      monthlyMajor: normalizedMonthlyMajor(draft.budget.monthlyMajor),
    },
  };
}

function normalizedMonthlyMajor(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return text;
  const [whole, fraction = ''] = text.split('.');
  return `${BigInt(whole)}.${fraction.padEnd(2, '0')}`;
}

function responseRevision(response, fallback = 0) {
  return finiteNumber(
    response?.configuration?.revision
      ?? response?.revision
      ?? response?.campaign?.revision,
    fallback,
  );
}

export async function recoverApprovedCampaignPreflight({
  requestApi = api,
  companyId,
  campaignId,
  expectedRevision,
} = {}) {
  const activeCompanyId = stringValue(companyId);
  const activeCampaignId = stringValue(campaignId);
  if (!activeCompanyId || !activeCampaignId || !Number.isSafeInteger(expectedRevision) || expectedRevision < 1) {
    const error = new Error('Campaign recovery request is invalid.');
    error.code = 'curated_preflight_recovery_request_invalid';
    error.status = 400;
    throw error;
  }
  const reopened = await requestApi.post(
    `/api/saas/campaigns/${encodeURIComponent(activeCampaignId)}/curated-configuration/recover-preflight`,
    { expectedRevision },
    tenantHeaders(activeCompanyId),
  );
  const revision = reopened?.configuration?.revision;
  const budgetMinimum = preflightBudgetMinimumView(reopened);
  if (
    reopened?.campaign?.id !== activeCampaignId
    || campaignStatus(reopened) !== 'draft'
    || !Number.isSafeInteger(revision)
    || revision !== expectedRevision + 1
    || preflightRecoveryView(reopened) !== null
    || budgetMinimum?.state !== 'available'
  ) {
    const error = new Error('Campaign recovery response is invalid.');
    error.code = 'curated_preflight_recovery_response_invalid';
    error.status = 503;
    throw error;
  }
  return reopened;
}

function preflightRecoveryActionGuardMatches(current, expected, inFlightRef, requestScope) {
  return inFlightRef?.current === requestScope
    && current?.scopeKey === expected.scopeKey
    && current?.companyId === expected.companyId
    && current?.campaignId === expected.campaignId
    && current?.revision === expected.revision
    && current?.recoveryIdentity === expected.recoveryIdentity
    && current?.canRecover === true;
}

export async function runApprovedPreflightRecoveryAction({
  requestApi = api,
  companyId,
  campaignId,
  expectedRevision,
  expectedScope,
  expectedRecoveryIdentity,
  inFlightRef,
  readGuard,
  requestConfirmation,
  onStart,
  onReopened,
  onFailure,
  onFinish,
} = {}) {
  const expected = Object.freeze({
    scopeKey: stringValue(expectedScope),
    companyId: stringValue(companyId),
    campaignId: stringValue(campaignId),
    revision: expectedRevision,
    recoveryIdentity: stringValue(expectedRecoveryIdentity),
  });
  if (
    !expected.scopeKey
    || !expected.companyId
    || !expected.campaignId
    || !Number.isSafeInteger(expected.revision)
    || expected.revision < 1
    || !inFlightRef
    || typeof readGuard !== 'function'
    || typeof requestConfirmation !== 'function'
  ) return Object.freeze({ state: 'invalid' });
  if (inFlightRef.current) return Object.freeze({ state: 'ignored' });

  const requestScope = `${expected.scopeKey}:preflight-recovery:${expected.revision}`;
  inFlightRef.current = requestScope;
  let started = false;
  const isCurrent = () => preflightRecoveryActionGuardMatches(
    readGuard(),
    expected,
    inFlightRef,
    requestScope,
  );
  const fail = (error) => {
    if (!isCurrent()) return Object.freeze({ state: 'stale' });
    onFailure?.(error);
    return Object.freeze({ state: 'failed', error });
  };

  try {
    let confirmed;
    try {
      confirmed = await requestConfirmation();
    } catch (error) {
      return fail(error);
    }
    if (!confirmed) return Object.freeze({ state: 'cancelled' });
    if (!isCurrent()) return Object.freeze({ state: 'stale' });

    started = true;
    onStart?.();
    let reopened;
    try {
      reopened = await recoverApprovedCampaignPreflight({
        requestApi,
        companyId: expected.companyId,
        campaignId: expected.campaignId,
        expectedRevision: expected.revision,
      });
    } catch (error) {
      return fail(error);
    }
    if (!isCurrent()) return Object.freeze({ state: 'stale' });
    onReopened?.(reopened);
    return Object.freeze({ state: 'reopened', reopened });
  } finally {
    if (started && isCurrent()) onFinish?.();
    if (inFlightRef.current === requestScope) inFlightRef.current = '';
  }
}

function budgetAccess(response, policyCanEditBudget) {
  const configuration = response?.configuration || {};
  const budget = configuration.budget || {};
  const canEdit = configuration.canEditBudget
    ?? budget.canEditBudget
    ?? response?.capabilities?.canEditBudget
    ?? response?.canEditBudget
    ?? policyCanEditBudget;
  return {
    canEdit: canEdit === true,
    lockedReason: stringValue(
      configuration.budgetLockedReason
      || budget.lockedReason
      || response?.budgetLockedReason,
    ),
  };
}

function dailyBudgetView(response) {
  const budget = response?.configuration?.budget || {};
  const dailyMinor = budget.dailyMinor
    ?? budget.dailyMinorUnits
    ?? budget.derivedDailyMinor
    ?? response?.validation?.derivedDailyMinor;
  return {
    minor: Number.isSafeInteger(Number(dailyMinor)) ? Number(dailyMinor) : null,
    currency: stringValue(budget.currency || budget.accountCurrency || response?.validation?.currency).toUpperCase(),
    rounding: stringValue(budget.rounding || budget.roundingRule || response?.validation?.budgetRounding),
    minimumMinor: Number.isSafeInteger(Number(budget.minimumDailyMinor))
      ? Number(budget.minimumDailyMinor)
      : null,
  };
}

export function normalizeApprovalReviewSnapshot(value) {
  if (!value || typeof value !== 'object' || value.schemaVersion !== 'meta.approval.v1') return null;
  const ads = (Array.isArray(value.ads) ? value.ads : []).map((ad) => ({
    renovationTypeId: stringValue(ad?.renovationTypeId),
    renovationTypeKey: stringValue(ad?.renovationTypeKey),
    renovationTypeName: stringValue(ad?.renovationTypeName) || 'Renovation type',
    packageName: stringValue(ad?.packageName) || 'Published creative',
    creativeVersionId: stringValue(ad?.creativeVersionId),
    primaryText: typeof ad?.primaryText === 'string' ? ad.primaryText : '',
    headline: typeof ad?.headline === 'string' ? ad.headline : '',
    callToAction: stringValue(ad?.callToAction) || 'LEARN_MORE',
  })).filter((ad) => ad.renovationTypeId && ad.creativeVersionId);
  const targeting = value.targeting && typeof value.targeting === 'object' ? value.targeting : {};
  const safeCities = (cities) => (Array.isArray(cities) ? cities : [])
    .map((city) => ({ key: stringValue(city?.key), label: stringValue(city?.label) }))
    .filter((city) => city.key && city.label);
  const meta = value.meta && typeof value.meta === 'object' ? value.meta : {};
  const budget = value.budget && typeof value.budget === 'object' ? value.budget : {};
  const structure = value.structure && typeof value.structure === 'object' ? value.structure : {};
  return Object.freeze({
    campaign: Object.freeze({
      id: stringValue(value.campaign?.id),
      name: stringValue(value.campaign?.name),
      configurationRevision: finiteNumber(value.campaign?.configurationRevision, 0),
    }),
    meta: Object.freeze({
      adAccountLabel: stringValue(meta.adAccount?.label),
      instantFormLabel: stringValue(meta.instantForm?.label),
      pageLabel: stringValue(meta.instantForm?.owningPage?.label),
      instagramLabel: stringValue(meta.instagramIdentity?.label),
    }),
    targeting: Object.freeze({
      country: ['CA', 'US'].includes(targeting.country) ? targeting.country : 'CA',
      includedCities: Object.freeze(safeCities(targeting.includedCities)),
      excludedCities: Object.freeze(safeCities(targeting.excludedCities)),
      radius: Object.freeze({
        value: finiteNumber(targeting.radius?.value, 0),
        unit: stringValue(targeting.radius?.unit),
      }),
    }),
    budget: Object.freeze({
      monthlyMajor: stringValue(budget.monthlyMajor),
      dailyMinor: Number.isSafeInteger(Number(budget.dailyMinor)) ? Number(budget.dailyMinor) : null,
      currency: stringValue(budget.currency).toUpperCase(),
      minimumDailyMinor: Number.isSafeInteger(Number(budget.minimumDailyMinor))
        ? Number(budget.minimumDailyMinor)
        : null,
      roundingRule: stringValue(budget.roundingRule),
    }),
    structure: Object.freeze({
      campaignCount: finiteNumber(structure.campaignCount, 1),
      adSetCount: finiteNumber(structure.adSetCount, 1),
      adCount: finiteNumber(structure.adCount, ads.length),
      initialStatus: stringValue(structure.initialStatus) || 'PAUSED',
    }),
    ads: Object.freeze(ads),
  });
}

export function approvalView(response) {
  const approval = response?.approval || {};
  return {
    workflowJobId: stringValue(approval.workflowJobId),
    approvalRequestId: stringValue(approval.approvalRequestId),
    status: stringValue(approval.status),
    snapshotHash: stringValue(approval.snapshotHash || approval.hash),
    invalidated: approval.invalidated === true,
    reviewSnapshot: normalizeApprovalReviewSnapshot(approval.reviewSnapshot),
  };
}

export function canCreateCuratedMetaPublish({
  status,
  approval,
  productPolicy,
  serverBlockers = [],
} = {}) {
  return hasNamedTenantAction(productPolicy, 'campaign.create_paused')
    && curatedMetaPublishRecovery(status, approval?.workflowJobId).canCreate
    && Boolean(curatedMetaPublishConfirmation(approval?.reviewSnapshot))
    && serverBlockers.length === 0;
}

function campaignStatus(response) {
  return stringValue(response?.campaign?.status) || 'draft';
}

export function isReconciledSubmissionState(response, campaignId) {
  return stringValue(response?.campaign?.id) === stringValue(campaignId)
    && ['awaiting_approval', 'approved', 'publishing'].includes(campaignStatus(response))
    && Boolean(approvalView(response).reviewSnapshot);
}

function campaignNameForDraft(options, draft) {
  const labels = options.catalog
    .filter((group) => draft.selectedTypeIds.includes(group.id))
    .map((group) => group.displayName);
  if (!labels.length) return 'Curated Meta campaign';
  return `${labels.slice(0, 2).join(' + ')}${labels.length > 2 ? ` + ${labels.length - 2} more` : ''} campaign`;
}

export async function saveCuratedCampaignDraft({
  requestApi = api,
  companyId,
  campaignId = '',
  brandId = '',
  options,
  draft,
  serverResponse = null,
  selectedTypes = [],
  policyCanEditBudget = false,
  isCurrent = () => true,
  onCampaignCreated = () => {},
} = {}) {
  let activeCampaignId = stringValue(campaignId);
  let createdCampaign = false;
  let responseBeforeSave = serverResponse;
  let draftToSave = draft;
  if (!activeCampaignId) {
    const created = await requestApi.post('/api/saas/campaigns', {
      ...(stringValue(brandId) ? { brandId: stringValue(brandId) } : {}),
      name: campaignNameForDraft(options, draft),
    }, tenantHeaders(companyId));
    activeCampaignId = stringValue(created?.campaign?.id);
    if (!activeCampaignId) throw new Error('campaign_create_response_invalid');
    createdCampaign = true;
    onCampaignCreated(activeCampaignId);
    if (!isCurrent()) return null;
  }
  // A campaign can exist even if its first configuration read/save failed.
  // Reload its revision before retrying that save, without creating a new draft.
  if (createdCampaign || !responseBeforeSave) {
    responseBeforeSave = await requestApi.get(
      `/api/saas/campaigns/${encodeURIComponent(activeCampaignId)}/curated-configuration`,
      tenantHeaders(companyId),
    );
    if (!isCurrent()) return null;
    if (!budgetAccess(responseBeforeSave, policyCanEditBudget).canEdit && !draft.budget.monthlyMajor) {
      draftToSave = {
        ...draft,
        budget: {
          monthlyMajor: String(responseBeforeSave?.configuration?.budget?.monthlyMajor || ''),
        },
      };
    }
  }
  const response = await requestApi.patch(
    `/api/saas/campaigns/${encodeURIComponent(activeCampaignId)}/curated-configuration`,
    buildCuratedConfigurationPayload(options, draftToSave, responseRevision(responseBeforeSave)),
    tenantHeaders(companyId),
  );
  if (!isCurrent()) return null;
  return Object.freeze({
    campaignId: activeCampaignId,
    response,
    createdCampaign,
    hydratedDraft: createCuratedDraft(options, selectedTypes, response),
  });
}

export async function startCampaignMetaStep({
  persistDraft,
  startOAuth = startCuratedMetaOAuth,
  companyId,
  credentialId = '',
  returnStep = 'meta',
  savedCampaignId = '',
  isCurrent = () => true,
  onSaved = () => {},
} = {}) {
  if (typeof persistDraft !== 'function' || typeof startOAuth !== 'function') return null;
  const nextStep = returnStep === 'review' ? 'review' : 'meta';
  // Reviewing a sealed or unchanged campaign must not rewrite its revision.
  const saved = savedCampaignId
    ? { campaignId: savedCampaignId }
    : await persistDraft({ nextStep, suppressNavigation: true });
  if (!saved?.campaignId || !isCurrent()) return null;
  onSaved(saved);
  const launch = await startOAuth({
    companyId,
    returnSurface: nextStep === 'review' ? 'campaign_review' : 'campaign_meta',
    campaignId: saved.campaignId,
    credentialId,
    isCurrent,
  });
  return Object.freeze({
    ...saved,
    launch,
    returnTo: `/#/curated-campaign/${encodeURIComponent(saved.campaignId)}?step=${nextStep}`,
  });
}

function recommendedFormEndpoint(credentialId, pageResourceId, suffix = '') {
  return `/api/saas/meta/connections/${encodeURIComponent(stringValue(credentialId))}`
    + `/pages/${encodeURIComponent(stringValue(pageResourceId))}/recommended-form${suffix}`;
}

function safeRetrySegment(value, fallback, maximumLength) {
  const normalized = stringValue(value).replace(/[^A-Za-z0-9:_-]/g, '_');
  return (normalized || fallback).slice(0, maximumLength);
}

function recommendedFormVerificationError() {
  const error = new Error('The created Instant Form could not be verified safely.');
  error.code = 'meta_recommended_form_verification_failed';
  return error;
}

function recommendedFormTemplateError() {
  const error = new Error('The recommended Instant Form preview is incomplete.');
  error.code = 'meta_recommended_form_template_mismatch';
  return error;
}

export async function previewRecommendedInstantFormForCampaign({
  requestApi = api,
  companyId,
  credentialId,
  pageResourceId,
  privacyPolicyUrl,
  followUpUrl,
} = {}) {
  return requestApi.post(
    recommendedFormEndpoint(credentialId, pageResourceId, '/preview'),
    {
      privacyPolicyUrl: stringValue(privacyPolicyUrl),
      followUpUrl: stringValue(followUpUrl),
    },
    tenantHeaders(companyId),
  );
}

// Rotation is a server-owned disposition, not an outcome-string inference:
// the backend sets newAttemptAllowed only after it has confirmed the terminal
// pair (operation + reservation) is settled, Meta provably holds no Form, and
// the outcome is remediable. Everything else keeps the key so replays stay
// idempotent against a possibly-written Form or a pending cleanup.
// The active-form conflict is deterministic for a Page: latch it only for its
// safe code, carry it across re-selection of the same Page, and drop it when
// the tenant moves to a different Page. Options commits clear it separately.
export function recommendedFormConflictAfterFailure(currentConflict, error, pageResourceId) {
  if (error?.code !== 'meta_recommended_form_active_form_conflict') return currentConflict;
  const page = stringValue(pageResourceId);
  return page ? Object.freeze({ pageResourceId: page }) : currentConflict;
}

export function recommendedFormConflictForPage(currentConflict, pageResourceId) {
  return currentConflict && currentConflict.pageResourceId === stringValue(pageResourceId)
    ? currentConflict
    : null;
}

export function evictRecommendedInstantFormRetryKey(cache, cacheIdentity, error) {
  if (!(cache instanceof Map)) return false;
  if (error?.newAttemptAllowed !== true) return false;
  return cache.delete(cacheIdentity);
}

export function scopedRecommendedInstantFormRetryKey(cache, {
  companyId,
  campaignId,
  credentialId,
  pageResourceId,
  templateDigest,
  privacyPolicyUrl,
  followUpUrl,
  createId = () => globalThis.crypto?.randomUUID?.(),
} = {}) {
  const requestIdentity = JSON.stringify([
    stringValue(companyId),
    stringValue(campaignId),
    stringValue(credentialId),
    stringValue(pageResourceId),
    stringValue(templateDigest),
    stringValue(privacyPolicyUrl),
    stringValue(followUpUrl),
  ]);
  const cacheIdentity = requestIdentity;
  let idempotencyKey = cache instanceof Map ? cache.get(cacheIdentity) : '';
  if (!idempotencyKey) {
    const nonce = stringValue(createId()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    idempotencyKey = [
      'recommended-form',
      safeRetrySegment(companyId, 'company', 48),
      safeRetrySegment(campaignId, 'campaign', 48),
      safeRetrySegment(pageResourceId, 'page', 48),
      safeRetrySegment(nonce, 'request', 48),
    ].join(':').slice(0, 200);
    if (cache instanceof Map) cache.set(cacheIdentity, idempotencyKey);
  }
  return Object.freeze({ cacheIdentity, idempotencyKey });
}

export async function createRecommendedInstantFormForCampaign({
  requestApi = api,
  companyId,
  credentialId,
  pageResourceId,
  privacyPolicyUrl,
  followUpUrl,
  templateDigest,
  idempotencyKey,
  campaignId,
} = {}) {
  const selectedCampaignId = stringValue(campaignId);
  if (!selectedCampaignId) throw recommendedFormVerificationError();
  const response = await requestApi.post(
    recommendedFormEndpoint(credentialId, pageResourceId),
    {
      privacyPolicyUrl: stringValue(privacyPolicyUrl),
      followUpUrl: stringValue(followUpUrl),
      templateDigest: stringValue(templateDigest),
      idempotencyKey: stringValue(idempotencyKey),
      campaignId: selectedCampaignId,
      confirmed: true,
    },
    {
      headers: {
        'x-company-id': companyId,
        'Idempotency-Key': stringValue(idempotencyKey),
      },
    },
  );
  let completedResponse = response;
  if (response?.operationId) {
    for (let attempt = 0; attempt < 30; attempt += 1) {
      if (completedResponse?.complete || completedResponse?.failed) break;
      await new Promise((resolve) => setTimeout(resolve, 1_000));
      completedResponse = await requestApi.get(
        `/api/saas/meta/recommended-form-operations/${encodeURIComponent(response.operationId)}`,
        tenantHeaders(companyId),
      );
    }
    if (!completedResponse?.complete) {
      // Surface the operation's safe terminal outcome so remediation guidance
      // (and retry-identity rotation) can react to it; only a still-pending
      // poll timeout stays generic.
      const outcome = stringValue(completedResponse?.outcome);
      if (completedResponse?.failed === true && /^[a-z][a-z0-9_]{0,119}$/.test(outcome)) {
        const failure = new Error('The recommended Instant Form request failed safely.');
        failure.code = outcome;
        if (typeof completedResponse?.newAttemptAllowed === 'boolean') {
          failure.newAttemptAllowed = completedResponse.newAttemptAllowed;
        }
        throw failure;
      }
      throw recommendedFormVerificationError();
    }
  }
  const optionResponse = Array.isArray(completedResponse?.connections)
    && completedResponse?.instantFormSetup
    ? {
        metaConnections: completedResponse.connections,
        instantFormSetup: completedResponse.instantFormSetup,
      }
    : await requestApi.get('/api/saas/curated-campaign-options', tenantHeaders(companyId));
  const normalizedOptions = normalizeCuratedOptions(optionResponse);
  const metaConnections = normalizedOptions.metaConnections;
  const returnedCredentialId = stringValue(completedResponse?.credentialId || credentialId);
  const returnedPageResourceId = stringValue(completedResponse?.createdPageResourceId || pageResourceId);
  const createdFormResourceId = stringValue(completedResponse?.createdFormResourceId || completedResponse?.instantFormResourceId);
  const connection = metaConnections.find((candidate) => candidate.credentialId === stringValue(credentialId));
  const page = connection?.pages.find((candidate) => candidate.resourceId === stringValue(pageResourceId));
  const form = connection?.instantForms.find((candidate) => (
    candidate.resourceId === createdFormResourceId
    && candidate.selectable
    && candidate.owningPage?.resourceId === stringValue(pageResourceId)
  ));
  if (
    !createdFormResourceId
    || returnedCredentialId !== stringValue(credentialId)
    || returnedPageResourceId !== stringValue(pageResourceId)
    || !connection
    || connection.connectionState !== 'ready'
    || connection.reconciliationStatus !== 'succeeded'
    || connection.appSubscriptionState !== 'ready'
    || connection.stale
    || connection.refreshRequired
    || !page
    || !form
  ) throw recommendedFormVerificationError();
  return Object.freeze({
    credentialId: returnedCredentialId,
    pageResourceId: returnedPageResourceId,
    instantFormResourceId: createdFormResourceId,
    metaConnections,
    instantFormSetup: normalizedOptions.instantFormSetup,
  });
}

export function applyRecommendedInstantFormSelection(draft, result) {
  const connections = Array.isArray(result?.metaConnections) ? result.metaConnections : [];
  const credentialId = stringValue(result?.credentialId);
  const matches = connections.filter((connection) => connection.credentialId === credentialId);
  const connection = matches.length === 1 ? matches[0] : null;
  const selectableAccounts = connection?.adAccounts.filter((account) => account.selectable) || [];
  const currentAccountResourceId = stringValue(draft?.meta?.adAccountResourceId);
  const currentAccountStillSelectable = selectableAccounts.some((account) => (
    account.resourceId === currentAccountResourceId
  ));
  const adAccountResourceId = currentAccountStillSelectable
    ? currentAccountResourceId
    : selectableAccounts.length === 1
      ? selectableAccounts[0].resourceId
      : '';
  return {
    ...draft,
    meta: {
      ...draft?.meta,
      adAccountResourceId,
      instantFormResourceId: stringValue(result?.instantFormResourceId),
      instagramIdentityResourceId: '',
    },
  };
}

export function campaignDraftForInstantFormMode(draft, formMode) {
  if (formMode !== 'create' || !draft?.meta) return draft;
  return {
    ...draft,
    meta: {
      ...draft.meta,
      instantFormResourceId: '',
      instagramIdentityResourceId: '',
    },
  };
}

function validationBlockers(response) {
  const validation = response?.validation || {};
  const values = Array.isArray(validation.blockedReasons)
    ? validation.blockedReasons
    : Array.isArray(validation.blockers)
    ? validation.blockers
    : Array.isArray(validation.errors)
      ? validation.errors
      : [];
  return values.slice(0, 12).map((blocker) => ({
    code: stringValue(blocker?.code || blocker) || 'curated_configuration_invalid',
  }));
}

function displayCurrency(value, currency) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || !currency) return 'Calculated after save';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function displayMinorCurrency(value, currency) {
  if (!Number.isSafeInteger(value) || !currency) return 'Calculated after save';
  try {
    const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency });
    const digits = formatter.resolvedOptions().maximumFractionDigits;
    return formatter.format(value / (10 ** digits));
  } catch {
    return `${value} minor units ${currency}`;
  }
}

function displayBudgetRounding(value) {
  if (value === 'monthly_minor_divided_by_30_4_round_half_up') {
    return '30.4-day divisor · round half up';
  }
  return value ? value.replaceAll('_', ' ') : '';
}

export default function CuratedCampaignSetup({
  session,
  navigate,
  selectedTypes = [],
  campaignId: routeCampaignId = '',
  initialStep = '',
}) {
  const companyId = session?.user?.activeCompany?.id || session?.user?.tenant?.companyId || '';
  const billing = useCompanyBilling();
  const routeScopeKey = `${companyId}:${routeCampaignId}:${selectedTypes.join(',')}:${initialStep}`;
  const scopeSequence = useRef(0);
  const campaignIdentity = useRef(null);
  if (!campaignIdentity.current || (
    routeScopeKey !== campaignIdentity.current.routeScopeKey
    && routeScopeKey !== campaignIdentity.current.adoptedRouteScopeKey
  )) {
    campaignIdentity.current = {
      scopeKey: `${routeScopeKey}:${++scopeSequence.current}`,
      routeScopeKey,
      campaignId: routeCampaignId,
    };
  }
  campaignIdentity.current.routeScopeKey = routeScopeKey;
  // Adopting this draft's first durable ID is the same editing session. Keep
  // its reviewed Form, retry keys and in-flight guards through that one route
  // replacement. Any other campaign, company, selection or step starts afresh.
  const scopeKey = campaignIdentity.current.scopeKey;
  const campaignId = campaignIdentity.current.campaignId;
  // scopeKey intentionally encodes the current selection and step so stale
  // responses are discarded when either changes. Signed media descriptors must not
  // inherit that, or adding a renovation type re-requests posters already on
  // screen, so the media cache uses a scope stable across selection and step.
  const mediaScopeKey = `${companyId}:${campaignId}:media`;
  const productPolicy = session?.user?.productPolicy;
  const canCreate = hasNamedTenantAction(productPolicy, 'campaign.draft.create');
  const canEditAction = hasNamedTenantAction(productPolicy, 'campaign.draft.edit');
  const canEdit = campaignId ? canEditAction : canCreate && canEditAction;
  const canCreatePaused = hasNamedTenantAction(productPolicy, 'campaign.create_paused') && billing.canCreate;
  const pausedCreationKeys = useRef(new Map());
  const canApprove = hasNamedTenantAction(productPolicy, 'campaign.approve');
  const policyCanEditBudget = hasNamedTenantAction(productPolicy, 'campaign.budget.update');
  const metaConnection = useCuratedMetaConnection({ companyId, enabled: Boolean(companyId), requestSync: false });
  // OGC-533: only a successful read can require renewal; failed or loading
  // reads never block, and temporary states only ask for a re-check.
  const metaAuthorizationState = useSettledMetaAuthorizationState(metaConnection, companyId);
  const [tenantState, setTenantState] = useState({ companyId: '', scopeKey: '', status: 'loading' });
  const [stepIndex, setStepIndex] = useState(0);
  const [draft, setDraft] = useState(null);
  const [serverResponse, setServerResponse] = useState(null);
  const [citySelections, setCitySelections] = useState({ scopeKey: null, locations: [] });
  const [sharedMetaSetup, setSharedMetaSetup] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState('');
  const [publishReconciliationRequired, setPublishReconciliationRequired] = useState(false);
  // The scope (companyId:campaignId:...) of the click that opened the staged
  // creation modal, or null when it is closed.
  const [pausedCreationModal, setPausedCreationModal] = useState(null);
  const confirmationDialog = useConfirmationDialog();
  const metaAuthorizationLauncher = useRef(createMetaAuthorizationLauncher());
  const metaRenewalGuard = useRef(null);
  // This choice is intentionally transient. Persisted drafts store only the
  // verified Form resource and always resume through the existing-Form path.
  const [instantFormMode, setInstantFormMode] = useState('existing');
  const [recommendedPageResourceId, setRecommendedPageResourceId] = useState('');
  const [recommendedForm, setRecommendedForm] = useState(null);
  // Page-keyed deterministic-conflict latch: an active-form conflict on a
  // Page cannot succeed on retry, so the create actions stay disabled with
  // inline remediation until the Page changes or refreshed Meta options
  // commit. Pending, queued, coalesced, and failed refreshes never commit
  // options, so they preserve the latch.
  const [recommendedFormConflict, setRecommendedFormConflict] = useState(null);
  useEffect(() => {
    setRecommendedFormConflict(null);
  }, [tenantState.options]);
  const requestVersion = useRef(0);
  const recommendedFormRetryKeys = useRef(new Map());
  const recommendedFormOperation = useRef('');
  const draftSaveInFlight = useRef(false);
  const publishInFlight = useRef('');
  const publishTerminalReconciliation = useRef('');
  const publishTerminalReconciliationRounds = useRef({ key: '', rounds: 0 });
  const refreshMetaCreationStatusRef = useRef(null);
  const publishGuardRef = useRef(null);
  const preflightRecoveryInFlight = useRef('');
  const preflightRecoveryGuardRef = useRef(null);
  const errorSummaryRef = useRef(null);
  const campaignStepsRef = useRef(null);
  const campaignFormRef = useRef(null);
  const pendingViewportAnchor = useRef(null);
  const pendingStepNavigation = useRef(null);
  const activeScopeRef = useRef(scopeKey);
  const previousMetaSync = useRef({ scopeKey: '', state: '', generation: 0 });
  activeScopeRef.current = scopeKey;
  const media = useCuratedCreativePreview({
    companyId,
    scopeKey,
    cacheScopeKey: mediaScopeKey,
    catalogEpoch: tenantState.catalogEpoch || 0,
  });

  useEffect(() => {
    const version = ++requestVersion.current;
    setTenantState({ companyId, scopeKey, status: companyId ? 'loading' : 'empty' });
    setDraft(null);
    setServerResponse(null);
    setDirty(false);
    setFeedback(null);
    setBusy('');
    setPublishReconciliationRequired(false);
    setPausedCreationModal(null);
    setStepIndex(0);
    setInstantFormMode('existing');
    setRecommendedPageResourceId('');
    setRecommendedForm(null);
    recommendedFormRetryKeys.current.clear();
    recommendedFormOperation.current = '';
    draftSaveInFlight.current = false;
    publishInFlight.current = '';
    publishTerminalReconciliation.current = '';
    publishTerminalReconciliationRounds.current = { key: '', rounds: 0 };
    preflightRecoveryInFlight.current = '';
    if (!companyId) return undefined;
    const requests = [
      api.get('/api/saas/curated-campaign-options', tenantHeaders(companyId)),
      ensureAndLoadCompanyBrands({ companyId }),
      campaignId
        ? api.get(
          `/api/saas/campaigns/${encodeURIComponent(campaignId)}/curated-configuration`,
          tenantHeaders(companyId),
        )
        : Promise.resolve(null),
    ];
    Promise.allSettled(requests).then(([optionResult, brandResult, configurationResult]) => {
      if (version !== requestVersion.current) return;
      if (configurationResult.status === 'rejected') throw configurationResult.reason;
      const configurationResponse = configurationResult.value;
      const loadedStatus = campaignStatus(configurationResponse);
      if (campaignId && shouldOpenCuratedCampaignDetail(loadedStatus)) {
        navigate?.(`campaigns/${encodeURIComponent(campaignId)}`);
        return;
      }
      const durableReview = campaignId
        && ['awaiting_approval', 'approved', 'publishing'].includes(loadedStatus)
        && Boolean(approvalView(configurationResponse).reviewSnapshot);
      if (optionResult.status === 'rejected' && !durableReview) throw optionResult.reason;
      if (brandResult.status === 'rejected' && !durableReview) throw brandResult.reason;
      const options = normalizeCuratedOptions(optionResult.status === 'fulfilled' ? optionResult.value : {});
      const brandResponse = brandResult.status === 'fulfilled' ? brandResult.value : {};
      const brands = Array.isArray(brandResponse?.brands) ? brandResponse.brands : [];
      setDraft(createCuratedDraft(options, selectedTypes, configurationResponse));
      setServerResponse(configurationResponse);
      setDirty(false);
      setTenantState({ companyId, scopeKey, catalogEpoch: version, status: 'ready', options, brands });
      const loadedPreflightRecovery = preflightRecoveryView(configurationResponse);
      const requestedStepIndex = campaignId
        ? STEPS.findIndex((candidate) => candidate.id === initialStep)
        : -1;
      const defaultStepIndex = ['awaiting_approval', 'approved', 'publishing'].includes(campaignStatus(configurationResponse))
        ? STEPS.findIndex((candidate) => candidate.id === 'review')
        : 0;
      setStepIndex(loadedPreflightRecovery
        ? STEPS.findIndex((candidate) => candidate.id === 'review')
        : requestedStepIndex >= 0 ? requestedStepIndex : defaultStepIndex);
    }).catch((error) => {
      if (version !== requestVersion.current) return;
      setTenantState({ companyId, scopeKey, status: 'error', error });
    });
    return () => {
      requestVersion.current += 1;
      pendingViewportAnchor.current = null;
      if (pendingStepNavigation.current?.targetScope !== activeScopeRef.current) {
        pendingStepNavigation.current = null;
      }
      if (activeScopeRef.current === scopeKey) activeScopeRef.current = '';
    };
  }, [scopeKey, navigate]);

  useBrowserLayoutEffect(() => {
    if (!pendingViewportAnchor.current) return;
    const snapshot = pendingViewportAnchor.current;
    pendingViewportAnchor.current = null;
    restoreTenantViewportAnchor(snapshot);
  }, [draft, feedback]);

  useBrowserLayoutEffect(() => {
    if (!pendingStepNavigation.current) return;
    const navigation = campaignStepsRef.current;
    const owner = navigation?.closest?.('[data-tenant-scroll-owner]');
    const heading = campaignFormRef.current?.querySelector('.bl-campaign-section-heading h2');
    if (!owner || !navigation || !heading) return;
    pendingStepNavigation.current = null;
    scrollCampaignStepIntoView({ owner, navigation, heading });
  }, [stepIndex]);

  const current = Boolean(companyId)
    && tenantState.companyId === companyId
    && tenantState.scopeKey === scopeKey;
  const baseOptions = current && tenantState.options ? tenantState.options : normalizeCuratedOptions();
  const options = useMemo(() => {
    const rows = [
      ...(current ? serverResponse?.configuration?.locationOptions || [] : []),
      ...(citySelections.scopeKey === scopeKey ? citySelections.locations : []),
    ];
    return { ...baseOptions, locations: {
      CA: normalizeLocations([...baseOptions.locations.CA, ...rows], 'CA'),
      US: normalizeLocations([...baseOptions.locations.US, ...rows], 'US'),
    } };
  }, [baseOptions, current, serverResponse, citySelections, scopeKey]);
  const searchCities = useCallback(async (country, query, signal) => {
    const result = await api.get(`/api/saas/curated-campaign-cities?${new URLSearchParams({ country, q: query })}`,
      { ...tenantHeaders(companyId), signal });
    if (activeScopeRef.current !== scopeKey || signal.aborted) return [];
    return normalizeLocations(result.locations, country);
  }, [companyId, scopeKey]);
  const metaAuthorizationUsable = curatedMetaAuthorizationUsable(
    metaConnection.payload || {},
  );
  const currentStatus = current ? tenantState.status : companyId ? 'loading' : 'empty';
  const metaCatalog = curatedMetaResourceCatalog(metaConnection.payload);
  const metaSyncState = String(metaCatalog?.syncState || '').toLowerCase();
  const metaSyncGeneration = Number(metaCatalog?.currentGeneration || 0);

  const metaConnectionStatus = metaConnection.status;
  // status is a real dependency: a ready frame whose state and generation
  // equal the loading frame's ('' / 0, e.g. no catalog row yet) must still set
  // the baseline, or the tenant's first sync would be measured against nothing.
  useEffect(() => {
    const observed = observeCuratedMetaSync(previousMetaSync.current, {
      status: metaConnectionStatus,
      scopeKey,
      state: metaSyncState,
      generation: metaSyncGeneration,
    });
    previousMetaSync.current = observed.next;
    if (!companyId || !observed.reload) return;
    const expectedScope = scopeKey;
    api.get('/api/saas/curated-campaign-options', tenantHeaders(companyId))
      .then((optionResponse) => {
        if (activeScopeRef.current !== expectedScope) return;
        setTenantState((currentState) => applyCuratedOptionsRefresh(currentState, {
          companyId,
          scopeKey,
          optionResponse,
        }));
        setFeedback({
          tone: 'success',
          message: 'Meta resources updated automatically.',
          preserveOnDraftEdit: true,
        });
      })
      .catch((error) => {
        if (activeScopeRef.current === expectedScope) {
          setFeedback({ tone: 'error', message: safeFailureMessage(error, 'integration') });
        }
      });
  }, [companyId, metaConnectionStatus, metaSyncGeneration, metaSyncState, scopeKey]);
  const access = budgetAccess(serverResponse || { configuration: { budget: draft?.budget } }, policyCanEditBudget);
  const serverCanEdit = serverResponse?.capabilities?.canEditDraft;
  const preflightBudgetMinimum = preflightBudgetMinimumView(serverResponse);
  const recoveredBudgetMinimum = preflightBudgetMinimum?.state === 'available'
    ? preflightBudgetMinimum
    : null;
  const preflightBudgetMinimumUnavailable = ['invalid', 'check_unavailable'].includes(
    preflightBudgetMinimum?.state,
  );
  const findings = useMemo(
    () => draft ? curatedDraftValidation(options, draft, {
      canEditBudget: access.canEdit,
      allowServerBudget: !campaignId && !access.canEdit,
      minimumMonthlyMajor: recoveredBudgetMinimum?.minimumMonthlyMajor,
      minimumCurrency: recoveredBudgetMinimum?.currency,
      minimumUnavailable: preflightBudgetMinimumUnavailable,
      preserveRecordedMeta: Boolean(serverResponse?.companyMetaSetup || serverResponse?.metaTarget),
    }) : [],
    [
      access.canEdit,
      campaignId,
      draft,
      options,
      preflightBudgetMinimumUnavailable,
      recoveredBudgetMinimum,
      serverResponse,
    ],
  );
  const step = STEPS[stepIndex];
  const metaStepBinding = campaignSetupBinding(serverResponse, options, draft)
    || (sharedMetaSetup?.companyId === companyId ? sharedMetaSetup.active : null);
  const metaRenewalBlocksStep = campaignMetaStepBlockedByRenewal({ binding: metaStepBinding, authorizationState: metaAuthorizationState });
  const selectedCount = draft?.selectedTypeIds.length || selectedTypes.length;
  const status = campaignStatus(serverResponse);
  const approval = approvalView(serverResponse);
  const preflightRecovery = preflightRecoveryView(serverResponse);
  const recoveryBlocksMetaCreation = preflightRecoveryBlocksMetaCreation(preflightRecovery);
  // OGC-533: a draft never stays past the Meta step while its destination's
  // access must be renewed (deep links, a destination adopted on save, or a
  // renewal discovered later). Changing steps keeps unsaved draft edits.
  const metaRenewalReturnStep = campaignMetaRenewalReturnStep({
    blocked: metaRenewalBlocksStep,
    draft: status === 'draft' && !preflightRecovery,
    stepIndex,
    metaIndex: STEPS.findIndex((candidate) => candidate.id === 'meta'),
  });
  useEffect(() => {
    if (metaRenewalReturnStep === null) return;
    goToStep(metaRenewalReturnStep);
    setFeedback({ tone: 'error', message: 'Renew Meta access before continuing this campaign.' });
  // goToStep is recreated each render; the return step alone drives this.
  }, [metaRenewalReturnStep]);
  const canRecoverPreflight = status === 'approved'
    && canEditAndReapprovePreflight(preflightRecovery, canApprove);
  const expectedPublishAdCount = Number.isSafeInteger(approval.reviewSnapshot?.structure?.adCount)
    ? approval.reviewSnapshot.structure.adCount
    : null;
  const hasPublishPermission = canCreatePaused;
  // Covers both the click itself (busy === 'publish') and reconnecting to an
  // already-publishing campaign (e.g. after a reload): both need the staged
  // "Preparing" view and a provisional (pre-binding) polling scope.
  const creationStaging = busy === 'publish' || status === 'publishing';
  const publishProgress = useCuratedMetaPublishProgress({
    campaignId,
    companyId,
    workflowJobId: approval.workflowJobId,
    approvalRequestId: approval.approvalRequestId,
    continueOnNotStarted: creationStaging,
    continueOnFailed: busy === 'publish',
    // OGC-438: the create request itself is the click→claim window (two Meta
    // verifications plus the seal) and its return restarts the loop; the
    // not-started bound applies only to reconnecting to an already-publishing
    // campaign.
    notStartedReadLimit: busy === 'publish' ? Infinity : undefined,
    enabled: Boolean(
      campaignId
      && (
        creationStaging
        || (status === 'approved' && hasPublishPermission && !recoveryBlocksMetaCreation)
      )
    ),
    totalAds: expectedPublishAdCount,
  });
  const pausedCreationModalOpen = Boolean(pausedCreationModal) && pausedCreationModal === scopeKey;
  const selectedAdCount = useMemo(
    () => curatedSelectedAdCount(options, draft),
    [options, draft],
  );
  // The modal's staged fallback may run before any sealed snapshot exists, so it
  // falls back to the currently selected ad count. The polling hook's own
  // totalAds input intentionally stays bound to the immutable snapshot.
  const pausedCreationModalAdCount = expectedPublishAdCount ?? selectedAdCount;
  const stagedPublishProgress = pausedCreationModalProgress({
    staging: creationStaging,
    progress: publishProgress.progress,
    expectedAds: pausedCreationModalAdCount,
  });
  const publishConfirmation = curatedMetaPublishConfirmation(approval.reviewSnapshot);
  const sealedReview = ['awaiting_approval', 'approved', 'publishing'].includes(status)
    && Boolean(approval.reviewSnapshot);
  const serverBlockers = [
    ...(status === 'approved' && approval.reviewSnapshot ? [] : validationBlockers(serverResponse)),
    ...(dirty ? [{ code: 'curated_campaign_unsaved_changes' }] : []),
    ...(['awaiting_approval', 'approved'].includes(status) && !approval.reviewSnapshot
      ? [{ code: 'curated_campaign_approval_snapshot_unavailable' }]
      : []),
  ];
  metaRenewalGuard.current = { scopeKey, metaConnection, busy };
  const renewalRequired = metaRenewalRequired(metaConnection.payload, [...serverBlockers, feedback]);
  const dailyBudget = dailyBudgetView(serverResponse);
  const accountSelection = draft ? selectedAccount(options, draft) : { connection: null, account: null };
  const formSelection = draft ? selectedForm(options, draft) : { connection: null, form: null };
  useEffect(() => {
    const reconciled = reconcileRecommendedFormUiState({
      formMode: instantFormMode,
      pageResourceId: recommendedPageResourceId,
      recommendedForm,
      conflict: recommendedFormConflict,
    }, options, accountSelection);
    setInstantFormMode(reconciled.formMode);
    setRecommendedPageResourceId(reconciled.pageResourceId);
    setRecommendedForm(reconciled.recommendedForm);
    setRecommendedFormConflict(reconciled.conflict);
    // The dispatched-request retry map is intentionally not part of this
    // transient UI reconciliation. A zero-Form -> Form refresh must clear stale
    // creation controls without changing the idempotency key for an ambiguous
    // provider write that still needs exact recovery.
  }, [
    accountSelection.account?.resourceId,
    accountSelection.connection?.credentialId,
    instantFormMode,
    options,
    recommendedForm,
    recommendedFormConflict,
    recommendedPageResourceId,
  ]);
  const editable = canEdit && serverCanEdit !== false && !recoveryBlocksMetaCreation && !busy;
  // OGC-531: the offering stays current while setup is open. Background reads
  // use the catalog only (no Meta), are bounded, never start or land while an
  // action runs, a preview is open or the review is sealed, and keep the view when nothing
  // customer-visible changed. Stale selections derive from the live catalog,
  // so a refresh never rewrites the draft.
  const catalogRefreshInFlight = useRef(false);
  const catalogRefreshBlocked = useRef(false);
  // An open preview defers the refresh, so a playing video is never closed
  // under the viewer; the next signal applies it after the dialog closes.
  catalogRefreshBlocked.current = Boolean(busy) || sealedReview || Boolean(media.preview);
  useCuratedCatalogRefresh(() => {
    if (!companyId || catalogRefreshBlocked.current || catalogRefreshInFlight.current) return;
    const expectedScope = scopeKey;
    catalogRefreshInFlight.current = true;
    readTenantCatalogInBackground(api, tenantHeaders(companyId))
      .then((response) => {
        if (activeScopeRef.current !== expectedScope || catalogRefreshBlocked.current) return;
        setTenantState((currentState) => applyCuratedCatalogRefresh(currentState, {
          companyId,
          scopeKey: expectedScope,
          catalog: response?.catalog,
        }));
      })
      .catch(() => { /* keep the current offering; the next signal retries */ })
      .finally(() => { catalogRefreshInFlight.current = false; });
  }, { enabled: currentStatus === 'ready' && Boolean(draft) });
  const publishProgressAllowsCreate = approvedCampaignPublishProgressAllowsCreate({
    status,
    hasRead: publishProgress.hasRead,
    readUnavailable: publishProgress.readUnavailable,
    progressState: publishProgress.progress.state,
    progress: publishProgress.progress,
  });
  const pausedCreationStalled = status === 'approved'
    && hasPublishPermission
    && stalledPausedCreationProgress(publishProgress.progress);
  const publishable = publishProgressAllowsCreate
    && !publishReconciliationRequired
    && !recoveryBlocksMetaCreation
    && canCreateCuratedMetaPublish({
      status,
      approval,
      productPolicy,
      serverBlockers,
    });
  const publishProgressDiscoveryPending = status === 'approved'
    && hasPublishPermission
    && !recoveryBlocksMetaCreation
    && !publishProgress.hasRead
    && !publishProgress.readUnavailable;
  const publishProgressDiscoveryUnavailable = status === 'approved'
    && hasPublishPermission
    && !recoveryBlocksMetaCreation
    && publishProgress.readUnavailable;
  publishGuardRef.current = {
    campaignId,
    companyId,
    publishConfirmation,
    publishable,
    revision: responseRevision(serverResponse),
    scopeKey,
    workflowJobId: approval.workflowJobId,
  };
  refreshMetaCreationStatusRef.current = refreshMetaCreationStatus;
  const recoveryRevision = responseRevision(serverResponse);
  const recoveryIdentity = preflightRecovery ? JSON.stringify(preflightRecovery) : '';
  preflightRecoveryGuardRef.current = {
    campaignId,
    canRecover: canRecoverPreflight,
    companyId,
    recoveryIdentity,
    revision: recoveryRevision,
    scopeKey,
  };

  useEffect(() => {
    const progress = publishProgress.progress;
    if (publishReconciliationRequired || !shouldReconcileCuratedMetaPublishTerminal({
      campaignId,
      busy,
      status,
      progressState: progress.state,
    })) return;
    const key = [
      scopeKey,
      approval.workflowJobId,
      approval.approvalRequestId,
      progress.state,
      progress.stage,
      progress.completedAds,
      progress.pausedAds,
    ].join(':');
    if (publishTerminalReconciliation.current === key) return;
    const nextRound = advanceCuratedMetaTerminalReconciliation(
      publishTerminalReconciliationRounds.current,
      key,
    );
    publishTerminalReconciliationRounds.current = nextRound;
    if (!nextRound.accepted) {
      setPublishReconciliationRequired(true);
      closePausedCreationModalForScope(scopeKey);
      return;
    }
    publishTerminalReconciliation.current = key;
    // A terminal intent can roll the campaign back to approved with a
    // server-declared recovery action. Reconcile the durable configuration
    // before choosing between setup and operational detail.
    void refreshMetaCreationStatusRef.current?.({
      retryPublishing: true,
      terminalReconciliationKey: key,
    });
  }, [
    approval.approvalRequestId,
    approval.workflowJobId,
    busy,
    campaignId,
    publishProgress.progress,
    publishReconciliationRequired,
    scopeKey,
    status,
  ]);

  function queueViewportAnchor(control) {
    const snapshot = captureTenantViewportAnchor(control);
    if (snapshot) pendingViewportAnchor.current = snapshot;
  }

  function updateDraft(updater, control = null) {
    queueViewportAnchor(control);
    setFeedback(feedbackAfterDraftEdit);
    setDirty(true);
    setDraft((currentDraft) => (
      typeof updater === 'function' ? updater(currentDraft) : updater
    ));
  }

  function toggleType(group, control = null) {
    const currentlySelected = draft.selectedTypeIds.includes(group.id);
    if (!editable || (!currentlySelected && !group.available)) return;
    updateDraft((currentDraft) => {
      const selected = currentDraft.selectedTypeIds.includes(group.id);
      if (selected) {
        // Removing a type removes only that type's pairs, live or stale.
        return {
          ...currentDraft,
          selectedTypeIds: currentDraft.selectedTypeIds.filter((id) => id !== group.id),
          selections: currentDraft.selections.filter((selection) => selection.renovationTypeId !== group.id),
        };
      }
      return {
        ...currentDraft,
        selectedTypeIds: [...currentDraft.selectedTypeIds, group.id],
        selections: group.choices.length >= MIN_CREATIVES && group.choices.length <= MAX_CREATIVES
          ? uniqueSelectionPairs([
            ...currentDraft.selections,
            ...group.choices.map((choice) => ({ renovationTypeId: group.id, creativeVersionId: choice.version.id })),
          ])
          : currentDraft.selections,
      };
    }, control);
  }

  function toggleCreative(group, versionId, control = null) {
    if (!editable || !draft.selectedTypeIds.includes(group.id)) return;
    const removing = curatedChoiceSelected(draft, group.id, versionId);
    // A creative moved between types may still be saved for its old type; it
    // cannot be picked here until that stale pair is replaced or removed, so
    // the old type's notice is never dropped silently.
    const savedElsewhere = removing ? null : draft.selections.find((selection) => (
      selection.creativeVersionId === versionId && selection.renovationTypeId !== group.id
    ));
    if (savedElsewhere) {
      const otherName = options.catalog.find((candidate) => candidate.id === savedElsewhere.renovationTypeId)?.displayName
        || 'another renovation type';
      queueViewportAnchor(control);
      setFeedback({ tone: 'error', message: `This ad is still saved for ${otherName}. Choose current ads for ${otherName} or remove that type first.` });
      return;
    }
    if (!removing && curatedSelectedChoices(draft, group).length >= MAX_CREATIVES) {
      queueViewportAnchor(control);
      setFeedback({ tone: 'error', message: `Choose at most two ${group.displayName} ads. Remove one before selecting another.` });
      return;
    }
    updateDraft((currentDraft) => {
      let selections = removing
        ? currentDraft.selections.filter((selection) => !(
          selection.renovationTypeId === group.id && selection.creativeVersionId === versionId
        ))
        : uniqueSelectionPairs([...currentDraft.selections, { renovationTypeId: group.id, creativeVersionId: versionId }]);
      const nextValidCount = curatedSelectedChoices({ selections }, group).length;
      // Choosing one or two current ads explicitly replaces that type's stale ads.
      if (nextValidCount >= MIN_CREATIVES && nextValidCount <= MAX_CREATIVES) {
        selections = selections.filter((selection) => (
          selection.renovationTypeId !== group.id
          || group.choices.some((choice) => choice.version.id === selection.creativeVersionId)
        ));
      }
      return { ...currentDraft, selections };
    }, control);
  }

  function setMetaField(field, value) {
    if (field === 'adAccountResourceId') {
      setInstantFormMode('existing');
      setRecommendedPageResourceId('');
      setRecommendedForm(null);
      recommendedFormOperation.current = '';
    }
    updateDraft((currentDraft) => {
      const nextMeta = { ...currentDraft.meta, [field]: value };
      if (field === 'adAccountResourceId') {
        const nextConnection = options.metaConnections.find((connection) => (
          connection.adAccounts.some((account) => account.resourceId === value)
        ));
        const currentFormAvailable = nextConnection?.instantForms.some((form) => (
          form.resourceId === currentDraft.meta.instantFormResourceId
        ));
        if (!currentFormAvailable) {
          nextMeta.instantFormResourceId = '';
          nextMeta.instagramIdentityResourceId = '';
        }
      }
      if (field === 'instantFormResourceId') nextMeta.instagramIdentityResourceId = '';
      return { ...currentDraft, meta: nextMeta };
    });
  }

  function changeInstantFormMode(nextMode) {
    if (
      busy
      || nextMode === instantFormMode
      || !['existing', 'create'].includes(nextMode)
    ) return;
    if (
      nextMode === 'create'
      && (
        metaConnection.capabilities?.canManage !== true
        || recommendedInstantFormCandidates(options, accountSelection).length === 0
      )
    ) return;

    setInstantFormMode(nextMode);
    setRecommendedPageResourceId('');
    setRecommendedForm(null);
    recommendedFormOperation.current = '';
    if (
      nextMode === 'create'
      && (draft.meta.instantFormResourceId || draft.meta.instagramIdentityResourceId)
    ) {
      updateDraft((currentDraft) => campaignDraftForInstantFormMode(currentDraft, nextMode));
      return;
    }
    setFeedback(null);
  }

  function setCountry(country) {
    if (!['CA', 'US'].includes(country)) return;
    const bounds = options.radiusBounds[country];
    updateDraft((currentDraft) => ({
      ...currentDraft,
      targeting: {
        country,
        includedCityKeys: [],
        excludedCityKeys: [],
        radius: { value: bounds.min, unit: bounds.unit },
      },
    }));
  }

  function addCity(kind, key, location) {
    if (!editable || !key) return false;
    const ownField = kind === 'included' ? 'includedCityKeys' : 'excludedCityKeys';
    const otherField = kind === 'included' ? 'excludedCityKeys' : 'includedCityKeys';
    if (draft.targeting[ownField].length >= 50) {
      setFeedback({ tone: 'error', message: 'You can select up to 50 cities in each list.' });
      return false;
    }

    if (draft.targeting[otherField].includes(key)) {
      setFeedback({
        tone: 'error',
        message: `That city is already ${kind === 'included' ? 'excluded' : 'included'}. Remove it there first.`,
      });
      return false;
    }
    if (location) {
      const keep = new Set([...draft.targeting.includedCityKeys, ...draft.targeting.excludedCityKeys]);
      setCitySelections((previous) => ({ scopeKey, locations: [
        ...(previous.scopeKey === scopeKey ? previous.locations.filter((row) => keep.has(row.key) && row.key !== key) : []),
        location,
      ] }));
    }
    updateDraft((currentDraft) => ({
      ...currentDraft,
      targeting: {
        ...currentDraft.targeting,
        [ownField]: [...new Set([...currentDraft.targeting[ownField], key])],
      },
    }));
    return true;
  }

  function removeCity(kind, key) {
    const field = kind === 'included' ? 'includedCityKeys' : 'excludedCityKeys';
    updateDraft((currentDraft) => ({
      ...currentDraft,
      targeting: {
        ...currentDraft.targeting,
        [field]: currentDraft.targeting[field].filter((candidate) => candidate !== key),
      },
    }));
  }

  function goToStep(index) {
    if (index < 0 || index >= STEPS.length || index === stepIndex) return;
    setFeedback(null);
    pendingStepNavigation.current = { targetScope: scopeKey };
    setStepIndex(index);
  }

  // OGC-533: Meta access the owner must renew blocks moving past the Meta
  // step from the footer and the stepper. A just-verified setup advances
  // through continueToTargeting, which proves fresh authorization.
  function metaRenewalStopsNavigation(targetIndex) {
    const metaIndex = STEPS.findIndex((candidate) => candidate.id === 'meta');
    if (!campaignMetaStepNavigationBlocked({ blocked: metaRenewalBlocksStep, currentIndex: stepIndex, targetIndex, metaIndex })) return false;
    setFeedback({ tone: 'error', message: 'Renew Meta access before continuing this campaign.' });
    return true;
  }

  async function continueFromStep() {
    if (step.id === 'meta' && metaRenewalStopsNavigation(stepIndex + 1)) return;
    const stepFindings = step.id === 'meta' ? [] : findings.filter((finding) => finding.step === step.id);
    if (stepFindings.length) {
      setFeedback({ tone: 'error', message: stepFindings[0].message });
      setTimeout(() => {
        const target = document.getElementById(stepFindings[0].field);
        (target || errorSummaryRef.current)?.focus?.();
      }, 0);
      return;
    }
    if (step.id === 'budget') {
      await persistDraft({ nextStep: 'review' });
      return;
    }
    goToStep(Math.min(stepIndex + 1, STEPS.length - 1));
  }

  async function continueToTargeting() {
    // Never advance past a save that cannot run right now.
    if (busy || draftSaveInFlight.current) return;
    if (editable) {
      await persistDraft({ nextStep: 'targeting' });
      return;
    }
    goToStep(STEPS.findIndex((candidate) => candidate.id === 'targeting'));
  }

  async function persistDraft({ exitAfter = false, nextStep = '', suppressNavigation = false } = {}) {
    if (!companyId || !draft || !canEdit || draftSaveInFlight.current) return null;
    draftSaveInFlight.current = true;
    if (!campaignIdentity.current.campaignId && !await billing.requestAccess('create')) {
      draftSaveInFlight.current = false;
      return null;
    }
    const expectedScope = scopeKey;
    setBusy(recommendedFormOperation.current === 'create' ? 'recommended-form-create' : 'save');
    setFeedback(null);
    try {
      const persisted = await saveCuratedCampaignDraft({
        companyId,
        campaignId: campaignIdentity.current.campaignId,
        brandId: tenantState.brands?.[0]?.id,
        options,
        draft,
        serverResponse,
        selectedTypes,
        policyCanEditBudget,
        isCurrent: () => activeScopeRef.current === expectedScope,
        onCampaignCreated: (createdCampaignId) => {
          if (activeScopeRef.current !== expectedScope) return;
          const savedStep = nextStep || STEPS[stepIndex].id;
          campaignIdentity.current.campaignId = createdCampaignId;
          campaignIdentity.current.adoptedRouteScopeKey = `${companyId}:${createdCampaignId}::${savedStep}`;
          navigate?.(`curated-campaign/${encodeURIComponent(createdCampaignId)}?step=${encodeURIComponent(savedStep)}`, { replace: true });
        },
      });
      if (!persisted) return null;
      setServerResponse(persisted.response);
      setDraft(persisted.hydratedDraft);
      setDirty(false);
      if (exitAfter) {
        navigate?.('campaigns');
      } else if (nextStep) {
        goToStep(Math.max(0, STEPS.findIndex((candidate) => candidate.id === nextStep)));
      } else if (!suppressNavigation) {
        setFeedback({ tone: 'success', message: 'Draft saved. You can safely leave and resume it from Campaigns.' });
      }
      return persisted;
    } catch (error) {
      if (activeScopeRef.current !== expectedScope) return null;
      if (error?.code === 'billing_subscription_required') await billing.refresh();
      if (activeScopeRef.current !== expectedScope) return null;
      setFeedback({ tone: 'error', message: safeFailureMessage(error, 'curated-campaign') });
      if (error?.code === 'curated_configuration_revision_stale' || error?.status === 409) {
        setFeedback({ tone: 'error', message: safeFailureMessage(error, 'curated-campaign') });
      }
      return null;
    } finally {
      if (activeScopeRef.current === expectedScope) {
        draftSaveInFlight.current = false;
        if (!recommendedFormOperation.current) setBusy('');
      }
    }
  }

  async function connectMetaFromCampaign({ fromSetup = false } = {}) {
    if (metaConnection.capabilities?.canManage !== true || busy) return;
    const expectedScope = scopeKey;
    const credentialId = metaCredentialId(metaConnection.connection);
    const isCurrent = () => {
      const guard = metaRenewalGuard.current;
      return activeScopeRef.current === expectedScope
        && guard.scopeKey === expectedScope
        && guard.metaConnection.capabilities?.canManage === true
        && (!credentialId || guard.metaConnection.capabilities?.canReauthorize === true)
        && metaCredentialId(guard.metaConnection.connection) === credentialId;
    };
    let saved = null;
    let started = false;
    try {
      await metaAuthorizationLauncher.current({
        credentialId,
        canManage: true,
        canReauthorize: metaConnection.capabilities?.canReauthorize === true,
        requestConfirmation: fromSetup ? async () => true : confirmationDialog.request,
        isCurrent,
        start: async () => {
          if (metaRenewalGuard.current.busy || draftSaveInFlight.current) return null;
          started = true;
          const result = await startCampaignMetaStep({
            persistDraft, companyId, credentialId, isCurrent,
            returnStep: step.id === 'review' ? 'review' : 'meta',
            savedCampaignId: campaignId && !dirty ? campaignId : '',
            onSaved: (value) => {
              saved = value;
              if (fromSetup) rememberCompanyMetaSetupLaunch({ companyId, surface: 'campaign_meta', campaignId: value.campaignId });
              if (!isCurrent()) return;
              setBusy('meta-connect');
              setFeedback(null);
            },
          });
          return result?.launch;
        },
      });
    } catch (error) {
      if (activeScopeRef.current !== expectedScope) return;
      setFeedback({ tone: 'error', message: safeFailureMessage(error, 'integration') });
      if (!campaignId && saved?.createdCampaign) {
        navigate?.(`curated-campaign/${encodeURIComponent(saved.campaignId)}?step=meta`, { replace: true });
      }
      // The setup modal shows its own failure; the page notice is for direct actions.
      if (fromSetup) throw error;
    } finally {
      if (started && activeScopeRef.current === expectedScope) setBusy('');
    }
  }

  async function refreshMetaForCampaign() {
    const credentialId = metaCredentialId(metaConnection.connection);
    if (metaConnection.capabilities?.canRefresh !== true || !credentialId || busy) return;
    setBusy('meta-refresh');
    setFeedback(null);
    try {
      const result = await refreshCampaignMetaOptions({
        companyId,
        credentialId,
        isCurrent: () => activeScopeRef.current === scopeKey,
        onRefreshCommitted: () => metaConnection.reload(),
      });
      if (result.outcome === 'stale' || activeScopeRef.current !== scopeKey) return;
      if (curatedMetaSyncQueued(result)) {
        setFeedback({
          tone: 'success',
          message: curatedMetaSyncRecoveryNotice(result)
            || 'Meta synchronization queued. Campaign options will update automatically.',
        });
        return;
      }
      if (result.outcome === 'options_reload_failed') {
        setFeedback({
          tone: 'error',
          message: 'Meta resources were refreshed, but campaign options could not be reloaded. Reload this page before choosing resources.',
        });
        return;
      }
      setTenantState((currentState) => applyCuratedOptionsRefresh(currentState, {
        companyId,
        scopeKey,
        optionResponse: result.optionResponse,
      }));
      setFeedback({ tone: 'success', message: 'Meta resources refreshed. Choose an ad account and Instant Form.' });
    } catch (error) {
      if (activeScopeRef.current === scopeKey) {
        setFeedback({ tone: 'error', message: safeFailureMessage(error, 'integration') });
        metaConnection.reload();
        try {
          const optionResponse = await api.get(
            '/api/saas/curated-campaign-options',
            tenantHeaders(companyId),
          );
          if (activeScopeRef.current === scopeKey) {
            setTenantState((currentState) => applyCuratedOptionsRefresh(currentState, {
              companyId,
              scopeKey,
              optionResponse,
            }));
          }
        } catch {
          // Preserve the original safe refresh failure while the connection read reloads independently.
        }
      }
    } finally {
      if (activeScopeRef.current === scopeKey) setBusy('');
    }
  }

  async function checkMetaAuthorizationForCampaign() {
    const credentialId = metaCredentialId(metaConnection.connection);
    if (metaConnection.capabilities?.canCheckAuthorization !== true || !credentialId || busy) return;
    const expectedScope = scopeKey;
    setBusy('meta-check');
    setFeedback(null);
    try {
      await checkCuratedMetaAuthorization({ companyId, credentialId });
      if (activeScopeRef.current !== expectedScope) return;
      setFeedback({ tone: 'success', message: 'Meta authorization check queued.' });
      metaConnection.reload();
    } catch (error) {
      if (activeScopeRef.current !== expectedScope) return;
      setFeedback({ tone: 'error', message: safeFailureMessage(error, 'integration') });
      metaConnection.reload();
    } finally {
      if (activeScopeRef.current === expectedScope) setBusy('');
    }
  }

  function chooseRecommendedFormPage(pageResourceId) {
    if (busy || !metaAuthorizationUsable) return;
    const candidate = findRecommendedInstantFormCandidate(
      options,
      accountSelection,
      pageResourceId,
    );
    setRecommendedPageResourceId(candidate?.page.resourceId || '');
    setRecommendedFormConflict((currentConflict) => (
      recommendedFormConflictForPage(currentConflict, candidate?.page.resourceId)
    ));
    setRecommendedForm(null);
    setFeedback(null);
  }

  function startRecommendedForm() {
    const candidate = findRecommendedInstantFormCandidate(
      options,
      accountSelection,
      recommendedPageResourceId,
    );
    if (
      busy
      || !metaAuthorizationUsable
      || metaConnection.capabilities?.canManage !== true
      || !candidate
    ) return;
    setRecommendedForm({
      status: 'edit',
      pageResourceId: candidate.page.resourceId,
      credentialId: candidate.connection.credentialId,
      mode: candidate.mode,
      privacyPolicyUrl: '',
      followUpUrl: '',
    });
    setFeedback(null);
  }

  function changeRecommendedFormField(field, value) {
    if (!['privacyPolicyUrl', 'followUpUrl'].includes(field) || busy) return;
    setRecommendedForm((currentForm) => (
      currentForm?.status === 'edit'
        ? { ...currentForm, [field]: value }
        : currentForm
    ));
    setFeedback(null);
  }

  function cancelRecommendedForm() {
    if (busy) return;
    setRecommendedForm(null);
    setFeedback(null);
  }

  async function previewRecommendedForm() {
    if (
      busy
      || recommendedFormOperation.current
      || recommendedForm?.status !== 'edit'
      || recommendedForm.pageResourceId !== recommendedPageResourceId
      || !metaAuthorizationUsable
      || metaConnection.capabilities?.canManage !== true
    ) return;
    const candidate = findRecommendedInstantFormCandidate(
      options,
      accountSelection,
      recommendedPageResourceId,
    );
    const connection = candidate?.connection;
    const page = candidate?.page;
    if (
      !connection?.credentialId
      || !page
      || recommendedForm.credentialId !== connection.credentialId
    ) return;
    const expectedScope = scopeKey;
    recommendedFormOperation.current = 'preview';
    setBusy('recommended-form-preview');
    setFeedback(null);
    try {
      const previewResponse = await previewRecommendedInstantFormForCampaign({
        companyId,
        credentialId: connection.credentialId,
        pageResourceId: page.resourceId,
        privacyPolicyUrl: recommendedForm.privacyPolicyUrl,
        followUpUrl: recommendedForm.followUpUrl,
      });
      if (activeScopeRef.current !== expectedScope) return;
      const reviewedPreview = previewResponse?.preview || previewResponse;
      if (!validRecommendedInstantFormPreview(reviewedPreview, {
        privacyPolicyUrl: recommendedForm.privacyPolicyUrl,
        followUpUrl: recommendedForm.followUpUrl,
      })) throw recommendedFormTemplateError();
      setRecommendedForm({
        ...recommendedForm,
        status: 'review',
        preview: reviewedPreview,
      });
    } catch (error) {
      if (activeScopeRef.current === expectedScope) {
        if (error?.code === 'meta_recommended_form_active_form_conflict') {
          // The inline Page alert is the single assertive announcement.
          setRecommendedFormConflict((currentConflict) => (
            recommendedFormConflictAfterFailure(currentConflict, error, page.resourceId)
          ));
        } else {
          setFeedback({ tone: 'error', message: safeFailureMessage(error, 'curated-campaign') });
        }
      }
    } finally {
      if (activeScopeRef.current === expectedScope) {
        recommendedFormOperation.current = '';
        setBusy('');
      }
    }
  }

  async function confirmRecommendedForm() {
    if (
      busy
      || draftSaveInFlight.current
      || recommendedFormOperation.current
      || recommendedForm?.status !== 'review'
      || recommendedForm.pageResourceId !== recommendedPageResourceId
      || !metaAuthorizationUsable
      || metaConnection.capabilities?.canManage !== true
    ) return;
    const candidate = findRecommendedInstantFormCandidate(
      options,
      accountSelection,
      recommendedPageResourceId,
    );
    const connection = candidate?.connection;
    const page = candidate?.page;
    if (
      !connection?.credentialId
      || !page
      || recommendedForm.credentialId !== connection.credentialId
    ) return;
    if (!validRecommendedInstantFormPreview(recommendedForm.preview, {
      privacyPolicyUrl: recommendedForm.privacyPolicyUrl,
      followUpUrl: recommendedForm.followUpUrl,
    })) {
      setFeedback({
        tone: 'error',
        message: safeFailureMessage(recommendedFormTemplateError(), 'curated-campaign'),
      });
      return;
    }
    const templateDigest = stringValue(recommendedForm.preview.templateDigest);
    const expectedScope = scopeKey;
    // Take the lock before the first await, including the initial campaign save.
    recommendedFormOperation.current = 'create';
    setBusy('recommended-form-create');
    setFeedback(null);
    let retry;
    try {
      let activeCampaignId = campaignIdentity.current.campaignId;
      if (!activeCampaignId || !serverResponse) {
        const saved = await persistDraft({ nextStep: 'meta', suppressNavigation: true });
        if (!saved?.campaignId || activeScopeRef.current !== expectedScope) return;
        activeCampaignId = saved.campaignId;
      }
      retry = scopedRecommendedInstantFormRetryKey(recommendedFormRetryKeys.current, {
        companyId,
        campaignId: activeCampaignId,
        credentialId: connection.credentialId,
        pageResourceId: page.resourceId,
        templateDigest,
        privacyPolicyUrl: recommendedForm.privacyPolicyUrl,
        followUpUrl: recommendedForm.followUpUrl,
      });
      setBusy('recommended-form-create');
      const result = await createRecommendedInstantFormForCampaign({
        companyId,
        credentialId: connection.credentialId,
        pageResourceId: page.resourceId,
        privacyPolicyUrl: recommendedForm.privacyPolicyUrl,
        followUpUrl: recommendedForm.followUpUrl,
        templateDigest,
        idempotencyKey: retry.idempotencyKey,
        campaignId: activeCampaignId,
      });
      if (activeScopeRef.current !== expectedScope) return;
      // Form finalization attaches the Form and advances the campaign revision.
      // Refresh that revision without overwriting the user's draft edits. If
      // the read fails, the next Save will reload it; Form creation is complete.
      let refreshedConfiguration = null;
      try {
        refreshedConfiguration = await api.get(
          `/api/saas/campaigns/${encodeURIComponent(activeCampaignId)}/curated-configuration`,
          tenantHeaders(companyId),
        );
      } catch { /* Keep the verified Form and invalidate the cached revision. */ }
      if (activeScopeRef.current !== expectedScope) return;
      setServerResponse(refreshedConfiguration);
      setTenantState((currentState) => {
        if (
          currentState.companyId !== companyId
          || currentState.scopeKey !== expectedScope
          || currentState.status !== 'ready'
        ) return currentState;
        return {
          ...currentState,
          options: Object.freeze({
            ...currentState.options,
            metaConnections: result.metaConnections,
            instantFormSetup: result.instantFormSetup,
          }),
        };
      });
      setDraft((currentDraft) => applyRecommendedInstantFormSelection(currentDraft, result));
      setDirty(true);
      setInstantFormMode('existing');
      setRecommendedPageResourceId('');
      setRecommendedForm(null);
      setFeedback({
        tone: refreshedConfiguration ? 'success' : 'warning',
        message: refreshedConfiguration
          ? 'Recommended Instant Form created, verified, and selected for this campaign.'
          : 'The verified Instant Form is selected. Save the draft to refresh its saved state before continuing.',
      });
    } catch (error) {
      evictRecommendedInstantFormRetryKey(
        recommendedFormRetryKeys.current,
        retry?.cacheIdentity,
        error,
      );
      if (activeScopeRef.current === expectedScope) {
        if (error?.code === 'meta_recommended_form_active_form_conflict') {
          // The inline Page alert is the single assertive announcement.
          setRecommendedFormConflict((currentConflict) => (
            recommendedFormConflictAfterFailure(currentConflict, error, page.resourceId)
          ));
        } else {
          setFeedback({ tone: 'error', message: safeFailureMessage(error, 'curated-campaign') });
        }
      }
    } finally {
      if (activeScopeRef.current === expectedScope) {
        recommendedFormOperation.current = '';
        setBusy('');
      }
    }
  }

  async function editAndReapprovePreflight() {
    if (
      !campaignId
      || !canRecoverPreflight
      || busy
      || preflightRecoveryInFlight.current
    ) return;
    const expectedScope = scopeKey;
    await runApprovedPreflightRecoveryAction({
      companyId,
      campaignId,
      expectedRevision: responseRevision(serverResponse),
      expectedScope,
      expectedRecoveryIdentity: JSON.stringify(preflightRecovery),
      inFlightRef: preflightRecoveryInFlight,
      readGuard: () => preflightRecoveryGuardRef.current,
      requestConfirmation: () => confirmationDialog.request({
        eyebrow: 'Campaign recovery',
        title: 'Edit this campaign budget?',
        description: 'No Meta campaign, ad set, or ads were created. BeyondLeads will preserve the original approval and failed creation attempt, then open a new draft revision. Review the corrected campaign before creating it in Meta — paused.',
        confirmLabel: 'Edit campaign',
        cancelLabel: 'Keep approved campaign',
        tone: 'warning',
      }),
      onStart() {
        setBusy('preflight-recovery');
        setFeedback(null);
      },
      onReopened(reopened) {
        setServerResponse(reopened);
        setDraft(createCuratedDraft(options, selectedTypes, reopened));
        setDirty(false);
        setPublishReconciliationRequired(false);
        pendingStepNavigation.current = { targetScope: expectedScope };
        setStepIndex(STEPS.findIndex((candidate) => candidate.id === 'budget'));
        setFeedback({
          tone: 'success',
          message: 'New draft revision opened. Update the budget, then save and review it before creating it in Meta — paused.',
        });
        setTimeout(() => {
          if (activeScopeRef.current !== expectedScope) return;
          const budgetInput = document.getElementById('monthly-budget');
          budgetInput?.focus?.();
          budgetInput?.select?.();
        }, 0);
      },
      onFailure(error) {
        setFeedback({ tone: 'error', message: safeFailureMessage(error, 'curated-campaign') });
        setTimeout(() => errorSummaryRef.current?.focus?.(), 0);
      },
      onFinish() {
        setBusy('');
      },
    });
  }

  async function rebuildReview() {
    if (!canCreatePaused || busy || !serverResponse?.creationReview?.canRebuild) return;
    const scope = scopeKey;
    setBusy('rebuild-review');
    try {
      await api.post(`/api/saas/campaigns/${encodeURIComponent(campaignId)}/curated-configuration/rebuild-review`, {
        expectedRevision: serverResponse.configuration.revision,
      }, tenantHeaders(companyId));
      if (scope !== activeScopeRef.current) return;
      const response = await api.get(`/api/saas/campaigns/${encodeURIComponent(campaignId)}/curated-configuration`, tenantHeaders(companyId));
      if (scope !== activeScopeRef.current) return;
      if (response?.campaign?.id !== campaignId) throw new Error('Campaign binding changed');
      setServerResponse(response);
      setDraft(createCuratedDraft(options, selectedTypes, response));
      setDirty(false);
      setStepIndex(0);
      setFeedback({ tone: 'success', message: 'Review rebuilt. Check and save the campaign settings before creating it.' });
    } catch {
      if (scope === activeScopeRef.current) setFeedback({ tone: 'warning', message: 'Review could not be rebuilt. Refresh the campaign before retrying.' });
    } finally {
      if (scope === activeScopeRef.current) setBusy('');
    }
  }

  async function refreshPausedCreationStatus({ expectedScope = scopeKey } = {}) {
    try {
      const refreshed = await api.get(`/api/saas/campaigns/${encodeURIComponent(campaignId)}/curated-configuration`, tenantHeaders(companyId));
      if (activeScopeRef.current !== expectedScope) return false;
      if (refreshed?.campaign?.id !== campaignId) throw new Error('Campaign binding changed');
      setServerResponse(refreshed);
      setDraft(createCuratedDraft(options, selectedTypes, refreshed));
      setDirty(false);
      setPublishReconciliationRequired(false);
      if (shouldOpenCuratedCampaignDetail(campaignStatus(refreshed))) {
        navigate?.(`campaigns/${encodeURIComponent(campaignId)}`);
      }
      return true;
    } catch {
      if (activeScopeRef.current === expectedScope) {
        setPublishReconciliationRequired(true);
        setFeedback({ tone: 'warning', message: 'Meta creation could not be reconciled. Refresh the durable status before retrying.' });
      }
      return false;
    }
  }

  // Programmatic close: never clears a modal that a newer click already owns.
  function closePausedCreationModalForScope(expectedScope) {
    setPausedCreationModal((currentScope) => (currentScope === expectedScope ? null : currentScope));
  }

  function closePausedCreationModal() {
    setPausedCreationModal(null);
    if (busy === 'publish') {
      setFeedback({ tone: 'info', message: pausedCreationProgressModalCopy.closedFeedback });
    }
  }

  function openPausedCreationCampaignPage() {
    setPausedCreationModal(null);
    if (campaignId) navigate?.(`campaigns/${encodeURIComponent(campaignId)}`);
  }

  async function createPausedFromReview() {
    if (!await billing.requestAccess('create')) return;
    const review = serverResponse?.creationReview;
    if (!canCreatePaused || review?.canCreate === false || !review?.ready || !campaignId || dirty || busy || publishInFlight.current || publishReconciliationRequired) return;
    const expectedScope = scopeKey;
    const requestKey = `${companyId}:${campaignId}:${review.expectedRevision}:${review.reviewHash}`;
    if (!pausedCreationKeys.current.has(requestKey)) pausedCreationKeys.current.set(requestKey, crypto.randomUUID());
    publishInFlight.current = requestKey;
    setBusy('publish');
    setPausedCreationModal(expectedScope);
    setFeedback(null);
    publishProgress.begin(expectedPublishAdCount);
    try {
      const result = await api.post(`/api/saas/campaigns/${encodeURIComponent(campaignId)}/curated-configuration/create-paused`, {
        expectedRevision: review.expectedRevision,
        expectedReviewHash: review.reviewHash,
        idempotencyKey: pausedCreationKeys.current.get(requestKey),
      }, tenantHeaders(companyId));
      if (activeScopeRef.current !== expectedScope) return;
      if (result.state === 'complete') {
        closePausedCreationModalForScope(expectedScope);
        navigate?.(`campaigns/${encodeURIComponent(campaignId)}`);
        return;
      }
      const reconciled = await refreshPausedCreationStatus({ expectedScope });
      // Creating keeps the staged view open; anything else must expose the
      // inline recovery notice and refresh control behind the modal.
      if (!reconciled || result.state !== 'creating') closePausedCreationModalForScope(expectedScope);
      if (reconciled) {
        setFeedback({ tone: result.state === 'creating' ? 'info' : 'warning', message: result.state === 'creating'
          ? 'Creating your paused campaign in Meta. You can return to its details to check progress.'
          : 'Creation needs attention. Review the saved recovery status before retrying.' });
      }
    } catch (error) {
      if (activeScopeRef.current !== expectedScope) return;
      if (error?.code === 'billing_subscription_required') {
        await billing.refresh();
        closePausedCreationModalForScope(expectedScope);
        if (activeScopeRef.current === expectedScope) setFeedback({ tone: 'error', message: safeFailureMessage(error, 'curated-campaign') });
        return;
      }
      const reconciled = await refreshPausedCreationStatus({ expectedScope });
      closePausedCreationModalForScope(expectedScope);
      if (reconciled) {
        // Verification refusals carry endpoint-approved recovery copy; every
        // other failure keeps the generic message (no provider detail).
        const recoverable = PAUSED_CREATION_RECOVERY_CODES.has(error?.code);
        setFeedback({ tone: 'warning',
          message: recoverable
            ? safeFailureMessage(error, 'curated-campaign')
            : 'Creation was not confirmed. The latest saved campaign status is shown.',
          code: recoverable ? error.code : null });
      }
    } finally {
      if (publishInFlight.current === requestKey) publishInFlight.current = '';
      if (activeScopeRef.current === expectedScope) setBusy('');
    }
  }

  async function refreshMetaCreationStatus({
    retryPublishing = false,
    terminalReconciliationKey = '',
  } = {}) {
    if (!campaignId || busy) return;
    const expectedScope = scopeKey;
    setBusy('publish-refresh');
    setFeedback(null);
    try {
      const refreshed = await loadDurableCuratedCampaignAfterTerminal({
        companyId,
        campaignId,
        maxAttempts: retryPublishing ? 4 : 1,
      });
      if (activeScopeRef.current !== expectedScope) return;
      const durableStatus = campaignStatus(refreshed);
      const durablePreflightRecovery = preflightRecoveryView(refreshed);
      setServerResponse(refreshed);
      setDraft(createCuratedDraft(options, selectedTypes, refreshed));
      setDirty(false);
      setPublishReconciliationRequired(false);
      if (durablePreflightRecovery) {
        pendingStepNavigation.current = { targetScope: expectedScope };
        setStepIndex(STEPS.findIndex((candidate) => candidate.id === 'review'));
        closePausedCreationModalForScope(expectedScope);
        return;
      }
      if (shouldOpenCuratedCampaignDetail(durableStatus)) {
        navigate?.(`campaigns/${encodeURIComponent(campaignId)}`);
        return;
      }
      if (durableStatus === 'publishing') {
        const automaticRound = Boolean(terminalReconciliationKey)
          && publishTerminalReconciliation.current === terminalReconciliationKey;
        if (automaticRound) publishTerminalReconciliation.current = '';
        const automaticRoundsExhausted = automaticRound
          && publishTerminalReconciliationRounds.current.key === terminalReconciliationKey
          && publishTerminalReconciliationRounds.current.rounds
            >= MAX_TERMINAL_RECONCILIATION_ROUNDS;
        // An accepted automatic round means the campaign is still on its
        // creating/still-publishing path, so the staged modal (if any) stays
        // open; every other outcome here needs the inline recovery notice.
        const reconciliationRequired = !automaticRound || automaticRoundsExhausted;
        setPublishReconciliationRequired(reconciliationRequired);
        if (reconciliationRequired) closePausedCreationModalForScope(expectedScope);
        setFeedback({
          tone: automaticRoundsExhausted ? 'error' : 'success',
          message: automaticRoundsExhausted
            ? 'Meta creation finished reporting progress, but the durable campaign state is still synchronizing. Refresh the durable status to continue safely.'
            : 'Meta creation is still running. This page is reconnecting to the same durable attempt.',
        });
        return;
      }
      setFeedback({
        tone: 'success',
        message: 'The latest durable campaign state is approved. You can safely retry paused creation.',
      });
    } catch (error) {
      if (activeScopeRef.current === expectedScope) {
        const automaticRound = Boolean(terminalReconciliationKey)
          && publishTerminalReconciliation.current === terminalReconciliationKey;
        if (automaticRound) publishTerminalReconciliation.current = '';
        const automaticRoundsExhausted = automaticRound
          && publishTerminalReconciliationRounds.current.key === terminalReconciliationKey
          && publishTerminalReconciliationRounds.current.rounds
            >= MAX_TERMINAL_RECONCILIATION_ROUNDS;
        setPublishReconciliationRequired(!automaticRound || automaticRoundsExhausted);
        closePausedCreationModalForScope(expectedScope);
        setFeedback({ tone: 'error', message: safeFailureMessage(error, 'curated-campaign') });
      }
    } finally {
      if (activeScopeRef.current === expectedScope) setBusy('');
    }
  }

  // Backend names once the catalog loads; until then a readable form of the
  // validated route keys.
  const catalogNames = new Map((options?.catalog || []).map(({ key, displayName }) => [key, displayName]));
  const queryLabels = (Array.isArray(selectedTypes) ? selectedTypes : [])
    .filter((key) => validRenovationTypeKey(key) && (!catalogNames.size || catalogNames.has(key)))
    .map((key) => catalogNames.get(key) || key.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase()));

  return (
    <section className="bl-campaign-setup" aria-labelledby="curated-campaign-title">
      {billing.enabled && !billing.canCreate && <BillingNotice navigate={navigate} contextualOnly />}
      <header className="bl-campaign-header">
        <div>
          <div className="bl-kicker">Curated campaign · Meta Ads</div>
          <h1 id="curated-campaign-title">Build a campaign from Published ads.</h1>
          <p>Select approved ads and only the Meta inputs needed for a paused lead campaign.</p>
          {queryLabels.length > 0 && !draft && (
            <small>Starting with {queryLabels.join(' · ')}</small>
          )}
        </div>
        <div className="bl-campaign-status-card" aria-live="polite">
          <span>Campaign status</span>
          <strong>{status === 'awaiting_approval' ? 'Ready to review' : status.replaceAll('_', ' ')}</strong>
          <small>{selectedAdCount} ads across {selectedCount} renovation type{selectedCount === 1 ? '' : 's'}</small>
        </div>
      </header>

      {currentStatus === 'loading' && <SetupNotice loading title="Loading campaign setup" body="Checking the tenant’s Published catalog and Meta resources." />}
      {currentStatus === 'empty' && <SetupNotice title="Choose a company first" body="Select an active company before configuring a campaign." />}
      {currentStatus === 'error' && (
        <SetupNotice
          tone="error"
          title="Campaign setup is temporarily unavailable"
          body={safeFailureMessage(tenantState.error, 'curated-campaign')}
        />
      )}

      {currentStatus === 'ready' && draft && (
        <>
          {/* tabIndex=-1: not part of tab order, but a stable, always-present
              target the paused-creation progress modal can return focus to
              on close (the create button itself may be disabled or gone by
              then). */}
          <nav ref={campaignStepsRef} className="bl-campaign-steps" aria-label="Campaign setup progress" tabIndex={-1}>
            {STEPS.map((candidate, index) => (
              <button
                type="button"
                key={candidate.id}
                aria-current={candidate.id === step.id ? 'step' : undefined}
                disabled={Boolean(busy) || recoveryBlocksMetaCreation}
                onClick={() => { if (!metaRenewalStopsNavigation(index)) goToStep(index); }}
              >
                <span>{index + 1}</span>
                {candidate.label}
              </button>
            ))}
          </nav>

          {/* The modal owns the staged view while it is open; rendering both
              would duplicate the heading id and its live regions. Both use the
              same staged fallback so an early Close never collapses this
              section back to nothing mid-creation. */}
          {!recoveryBlocksMetaCreation && !pausedCreationModalOpen && (
            <CuratedMetaPublishProgress progress={stagedPublishProgress} companyId={companyId} campaignId={campaignId} />
          )}

          {step.id === 'review' && status === 'approved' && preflightRecovery && (
            <PreflightRecoveryNotice
              recovery={preflightRecovery}
              canEditAndReapprove={canRecoverPreflight}
              busy={busy}
              onEditAndReapprove={editAndReapprovePreflight}
              onRefresh={refreshMetaCreationStatus}
            />
          )}

          {feedback && (
            <div
              className={`bl-campaign-feedback is-${feedback.tone}`}
              role={feedback.tone === 'error' ? 'alert' : 'status'}
              tabIndex={feedback.tone === 'error' ? -1 : undefined}
              ref={feedback.tone === 'error' ? errorSummaryRef : undefined}
            >
              {feedback.message}
            </div>
          )}

          {approval.invalidated && (
            <SetupNotice
              compact
              tone="warning"
              title="Approval was invalidated"
              body="Any saved change creates a new revision and requires a new immutable approval snapshot."
            />
          )}

          {step.id === 'review' && renewalRequired && (
            <div className="bl-campaign-feedback is-warning" role="status">
              <p>{metaConnection.capabilities?.canReauthorize
                ? 'Renew Meta authorization, then review readiness again. Your campaign approval and publish state will stay unchanged.'
                : 'Ask a company owner or admin to renew Meta authorization, then check readiness again.'}</p>
              <CuratedMetaConnectionActions
                payload={metaConnection.payload}
                canManage={metaConnection.capabilities?.canManage === true}
                canReauthorize={metaConnection.capabilities?.canReauthorize === true}
                busy={busy ? 'connect' : ''}
                onConnect={connectMetaFromCampaign}
              />
            </div>
          )}

          {step.id === 'meta' && metaRenewalBlocksStep && (
            <div className="bl-campaign-feedback is-warning" role="status">
              <p>{metaConnection.capabilities?.canReauthorize
                ? 'Renew Meta authorization to continue this campaign. The saved Meta destination stays unchanged.'
                : 'Ask a company owner or admin to renew Meta authorization, then continue this campaign.'}</p>
              <CuratedMetaConnectionActions
                payload={metaConnection.payload}
                canManage={metaConnection.capabilities?.canManage === true}
                canReauthorize={metaConnection.capabilities?.canReauthorize === true}
                busy={busy ? 'connect' : ''}
                onConnect={connectMetaFromCampaign}
              />
            </div>
          )}

          <form ref={campaignFormRef} onSubmit={(event) => event.preventDefault()}>
            {step.id === 'creative' && (
              <CreativeStep
                companyId={companyId}
                scopeKey={scopeKey}
                mediaScopeKey={mediaScopeKey}
                catalogEpoch={tenantState.catalogEpoch}
                options={options}
                draft={draft}
                editable={editable}
                onToggleType={toggleType}
                onToggleCreative={toggleCreative}
                onQuickPlay={media.openQuickPlay}
                onPreview={media.openDetails}
              />
            )}
            {step.id === 'meta' && (
              <section className="bl-campaign-panel" aria-labelledby="company-meta-step-title">
                <StepHeading
                  kicker="Step 2 of 5"
                  title="Use the company’s Meta setup"
                  body={metaRenewalBlocksStep
                    ? 'Renew Meta access to continue with this campaign.'
                    : setupReady(metaStepBinding)
                      ? 'Your company’s Meta assets are ready for this campaign.'
                      : metaStepBinding ? 'Review the Meta destination saved for this campaign.' : 'Connect Meta and choose where campaign leads will go.'}
                />
                <h3 id="company-meta-step-title" className="bl-review-summary-title">Meta destination</h3>
                <CompanyMetaSetup
                  companyId={companyId}
                  companyName={session?.user?.activeCompany?.name || 'Company'}
                  mode="campaign"
                  campaignId={campaignId}
                  configurationResponse={serverResponse}
                  options={options}
                  draft={draft}
                  onStateChange={setSharedMetaSetup}
                  metaConnection={metaConnection}
                  onAuthorize={() => connectMetaFromCampaign({ fromSetup: true })}
                  onCheckAuthorization={checkMetaAuthorizationForCampaign}
                  onRefresh={refreshMetaForCampaign}
                  campaignStep={{ onContinue: continueToTargeting, busy: Boolean(busy) }}
                  onComplete={async (_next, { advance = true } = {}) => {
                    metaConnection.reload();
                    // OGC-527: verified setup advances to Targeting only after the
                    // draft saves; a failed save keeps this step and its feedback.
                    // Closing a finished setup records the draft and stays here.
                    if (advance) await continueToTargeting();
                    else if (editable) await persistDraft({ nextStep: 'meta', suppressNavigation: true });
                    const optionResponse = await api.get('/api/saas/curated-campaign-options', tenantHeaders(companyId)).catch(() => null);
                    if (optionResponse && activeScopeRef.current === scopeKey) setTenantState((currentState) => applyCuratedOptionsRefresh(currentState, { companyId, scopeKey, optionResponse }));
                  }}
                />
                {!serverResponse?.companyMetaSetup
                  && sharedMetaSetup?.companyId === companyId
                  && sharedMetaSetup?.campaignId === campaignId
                  && !sharedMetaSetup?.pending?.operationId
                  && options.instantFormSetup.state === 'recovery_available' && (
                  <details style={{ marginTop: 16 }}><summary>Recover a previous campaign Form request</summary>
              <MetaStep
                options={options}
                draft={draft}
                editable={editable}
                accountSelection={accountSelection}
                formSelection={formSelection}
                formMode={instantFormMode}
                metaConnection={metaConnection}
                connectionBusy={busy === 'meta-connect'
                  ? 'connect'
                  : busy === 'meta-check'
                    ? 'check'
                    : busy === 'meta-refresh'
                      ? 'refresh'
                      : ''}
                recommendedPageResourceId={recommendedPageResourceId}
                recommendedForm={recommendedForm}
                recommendedConflict={Boolean(recommendedFormConflict
                  && recommendedFormConflict.pageResourceId === recommendedPageResourceId)}
                recommendedBusy={busy.startsWith('recommended-form')}
                onChange={setMetaField}
                onFormModeChange={changeInstantFormMode}
                onConnect={connectMetaFromCampaign}
                onCheckAuthorization={checkMetaAuthorizationForCampaign}
                onRefresh={refreshMetaForCampaign}
                onRecommendedPageChange={chooseRecommendedFormPage}
                onStartRecommendedForm={startRecommendedForm}
                onRecommendedFormChange={changeRecommendedFormField}
                onPreviewRecommendedForm={previewRecommendedForm}
                onConfirmRecommendedForm={confirmRecommendedForm}
                onCancelRecommendedForm={cancelRecommendedForm}
              />
                  </details>
                )}
              </section>
            )}
            {step.id === 'targeting' && (
              <TargetingStep
                options={options}
                draft={draft}
                editable={editable}
                onCountry={setCountry}
                onSearchCities={searchCities}
                onAddCity={addCity}
                onRemoveCity={removeCity}
                onRadius={(value) => updateDraft((currentDraft) => ({
                  ...currentDraft,
                  targeting: {
                    ...currentDraft.targeting,
                    radius: { ...currentDraft.targeting.radius, value },
                  },
                }))}
              />
            )}
            {step.id === 'budget' && (
              <BudgetStep
                draft={draft}
                currency={stringValue(accountSelection.account?.currency).toUpperCase()}
                dailyBudget={dailyBudget}
                canEdit={editable && access.canEdit}
                providerMinimum={recoveredBudgetMinimum}
                providerMinimumUnavailable={preflightBudgetMinimumUnavailable}
                lockedReason={access.lockedReason || (
                  !campaignId && !draft.budget.monthlyMajor
                    ? 'Your role will use the company’s server-owned default budget when this campaign is first saved.'
                    : ''
                )}
                onChange={(value) => updateDraft((currentDraft) => ({
                  ...currentDraft,
                  budget: { monthlyMajor: value },
                }))}
              />
            )}
            {step.id === 'review' && (
              <ReviewStep
                options={options}
                draft={draft}
                findings={sealedReview ? [] : findings}
                serverBlockers={serverBlockers}
                status={status}
                accountSelection={accountSelection}
                formSelection={formSelection}
                dailyBudget={dailyBudget}
                reviewSnapshot={approval.reviewSnapshot}
                recordedDestination={serverResponse?.companyMetaSetup?.destination || serverResponse?.metaTarget?.destination}
                hasPreflightRecovery={recoveryBlocksMetaCreation}
                onPreview={media.openDetails}
              />
            )}
          </form>

          <footer className="bl-campaign-actions">
            {canEdit && <button type="button" className="is-ghost" onClick={() => persistDraft({ exitAfter: true })} disabled={!editable}>
              Save &amp; exit
            </button>}
            <div>
              {stepIndex > 0 && <button type="button" className="is-ghost" onClick={() => goToStep(stepIndex - 1)} disabled={Boolean(busy) || recoveryBlocksMetaCreation}>Back</button>}
              {step.id !== 'review' && (
                <button type="button" onClick={continueFromStep} disabled={Boolean(busy)}>
                  {step.id === 'budget' ? (busy === 'save' ? 'Saving…' : 'Save & review') : 'Continue'}
                </button>
              )}
              {step.id === 'review' && canCreatePaused && serverResponse?.creationReview?.canRebuild && (
                <button type="button" onClick={rebuildReview} disabled={Boolean(busy)}>
                  {busy === 'rebuild-review' ? 'Rebuilding review…' : 'Rebuild review'}
                </button>
              )}
              {step.id === 'review' && canCreatePaused && pausedCreationControlVisible({
                busy,
                status,
                canCreate: serverResponse?.creationReview?.canCreate,
                publishReconciliationRequired,
                publishProgressAllowsCreate,
              }) && (
                <button type="button" onClick={createPausedFromReview}
                  disabled={Boolean(busy) || dirty || !serverResponse?.creationReview?.ready || recoveryBlocksMetaCreation}>
                  {busy === 'publish' ? 'Creating in Meta…' : 'Create campaign in Meta — paused'}
                </button>
              )}
              {step.id === 'review'
                && ((publishReconciliationRequired && hasPublishPermission) || pausedCreationStalled) && (
                <button type="button" onClick={() => refreshPausedCreationStatus()} disabled={Boolean(busy)} aria-busy={busy === 'publish-refresh' ? 'true' : undefined}>
                  {busy === 'publish-refresh' ? <><Beacon size="inline" className="is-current" delayMs={0} /> Refreshing Meta creation status…</> : 'Refresh Meta creation status'}
                </button>
              )}
              {step.id === 'review' && canCreatePaused && serverResponse?.creationReview?.canCreate === false && (
                <span>Ask a company owner or admin to continue this recovery.</span>
              )}
              {step.id === 'review' && publishProgressDiscoveryPending && (
                <span className="bl-approved-label"><Beacon size="inline" delayMs={0} /> Checking Meta creation status…</span>
              )}
              {step.id === 'review' && publishProgressDiscoveryUnavailable && !publishReconciliationRequired && (
                <button type="button" className="is-secondary" onClick={publishProgress.reconnect} disabled={Boolean(busy)}>
                  Retry Meta creation status
                </button>
              )}
              {step.id === 'review'
                && status === 'approved'
                && !publishable
                && !publishProgressDiscoveryPending
                && !publishProgressDiscoveryUnavailable
                && !(publishReconciliationRequired && hasPublishPermission)
                && <span className="bl-approved-label"><IconCheck size={16} /> Ready to create</span>}
            </div>
            {canEdit && <button type="button" className="is-save" onClick={() => persistDraft()} disabled={!editable}>
              {busy === 'save' ? 'Saving…' : 'Save draft'}
            </button>}
          </footer>
        </>
      )}

      {current && media.preview?.mode === 'quick' && (
        <QuickPlayDialog
          preview={media.preview}
          onClose={media.closePreview}
          onMediaError={media.markMediaUnavailable}
        />
      )}
      {current && media.preview?.mode === 'detail' && (
        <CreativePreviewDialog
          preview={media.preview}
          onClose={media.closePreview}
          onMediaError={media.markMediaUnavailable}
        />
      )}
      {pausedCreationModalOpen && (
        <PausedCreationProgressModal
          progress={stagedPublishProgress}
          companyId={companyId}
          campaignId={campaignId}
          onClose={closePausedCreationModal}
          onOpenCampaign={openPausedCreationCampaignPage}
          returnFocus={campaignStepsRef.current}
        />
      )}
      <ConfirmationDialog {...confirmationDialog.dialogProps} />
    </section>
  );
}

function StepHeading({ kicker, title, body }) {
  return (
    <header className="bl-campaign-section-heading">
      <div className="bl-kicker">{kicker}</div>
      <h2 tabIndex={-1}>{title}</h2>
      <p>{body}</p>
    </header>
  );
}

function creativeTitle(entry, index) {
  return entry.package?.displayName || entry.version.headline || `Creative ${index + 1}`;
}

export function CreativeStep({
  companyId,
  scopeKey,
  mediaScopeKey,
  catalogEpoch,
  options,
  draft,
  editable,
  onToggleType,
  onToggleCreative,
  onQuickPlay,
  onPreview,
}) {
  const [expanded, setExpanded] = useState(() => new Set());
  const staleTypeIds = new Set(curatedStaleSelections(options, draft).map((selection) => selection.renovationTypeId));
  const unavailableTypeIds = draft.selectedTypeIds.filter((typeId) => (
    !options.catalog.some((group) => group.id === typeId)
  ));
  function toggleExpanded(group, control) {
    const opening = !expanded.has(group.id);
    setExpanded((current) => {
      const next = new Set(current);
      if (opening) next.add(group.id); else next.delete(group.id);
      return next;
    });
    if (!opening) requestAnimationFrame(() => control?.scrollIntoView?.({ block: 'nearest' }));
  }
  // A selected ad can sit past the collapsed rows; its shortcut opens the
  // section and moves focus to that ad's selection control.
  function showSelectedCreative(group, versionId) {
    setExpanded((current) => (current.has(group.id) ? current : new Set([...current, group.id])));
    requestAnimationFrame(() => {
      const card = document.getElementById(`creative-${group.id}-${versionId}`);
      card?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      card?.querySelector('input[type="checkbox"]')?.focus({ preventScroll: true });
    });
  }
  return (
    <section className="bl-campaign-panel" aria-labelledby="creative-step-title">
      <StepHeading
        kicker="Step 1 of 5"
        title="Choose renovation types and ads"
        body="Include your renovation types, then choose one or two approved ads for each."
      />
      <fieldset className="bl-campaign-fieldset" id="renovation-types" tabIndex="-1">
        <legend id="creative-step-title">Published renovation types</legend>
        <div className="bl-type-grid">
          {unavailableTypeIds.map((typeId) => (
            <article className="bl-type-panel is-selected" key={typeId} id={`type-${typeId}`} tabIndex="-1">
              <div className="bl-type-panel-heading">
                <div><span className="bl-published-pill">Reselection required</span><h3>Unavailable renovation type</h3><p>A saved creative is no longer in the Published catalog.</p></div>
                <button type="button" className="bl-remove-stale-type" disabled={!editable} onClick={(event) => onToggleType({ id: typeId, choices: [], available: false }, event.currentTarget)}>Remove type</button>
              </div>
            </article>
          ))}
          {options.catalog.map((group) => {
            const selected = draft.selectedTypeIds.includes(group.id);
            const selectedVersions = curatedSelectedChoices(draft, group);
            const count = group.choices.length;
            const groupExpanded = expanded.has(group.id);
            const collapsible = count > COLLAPSED_CREATIVE_COUNT;
            const gridId = `cards-${group.id}`;
            return (
              <article className={`bl-type-panel${selected ? ' is-selected' : ''}`} key={group.id} id={`type-${group.id}`} tabIndex="-1">
                <div className="bl-type-panel-heading">
                  <div>
                    <span className="bl-published-pill">Published</span>
                    <h3>{group.displayName}</h3>
                    <p>{count} approved {count === 1 ? 'choice' : 'choices'} available</p>
                  </div>
                  <label className="bl-type-selector">
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={!editable}
                      aria-label={`${selected ? 'Remove' : 'Include'} ${group.displayName}`}
                      onChange={(event) => onToggleType(group, event.currentTarget)}
                    />
                    <span><IconCheck size={15} /> {selected ? 'Included' : 'Include type'}</span>
                  </label>
                </div>
                {selected && (
                  <fieldset className="bl-creative-fieldset">
                    <legend>
                      <strong>{selectedVersions.length} of {MAX_CREATIVES} ads selected</strong>
                      <span className="bl-selection-hint">Choose at least one. Preview any ad before selecting it.</span>
                    </legend>
                    {selectedVersions.length > 0 && (
                      <div className="bl-selected-ad-summary">
                        <span>Selected:</span>
                        {selectedVersions.map((entry) => {
                          const index = group.choices.indexOf(entry);
                          return (
                            <button
                              type="button"
                              key={entry.version.id}
                              aria-label={`Show selected ${group.displayName} — ${creativeTitle(entry, index)}`}
                              onClick={() => showSelectedCreative(group, entry.version.id)}
                            >
                              ✓ {creativeTitle(entry, index)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {staleTypeIds.has(group.id) && (
                      <SetupNotice compact tone="warning" title="A saved ad is no longer Published" body="Select one or two current choices to replace the stale version." />
                    )}
                    <div className={`bl-setup-creative-grid${count === 1 ? ' is-single' : ''}`} id={gridId}>
                      {group.choices.map((entry, index) => {
                        const choiceSelected = curatedChoiceSelected(draft, group.id, entry.version.id);
                        return (
                          <article
                            className={`bl-setup-creative${choiceSelected ? ' is-selected' : ''}`}
                            key={entry.version.id}
                            id={`creative-${group.id}-${entry.version.id}`}
                            hidden={collapsible && !groupExpanded && index >= COLLAPSED_CREATIVE_COUNT}
                          >
                            <label>
                              <input
                                type="checkbox"
                                checked={choiceSelected}
                                disabled={!editable}
                                aria-label={`${choiceSelected ? 'Remove' : 'Select'} ${entry.package?.displayName || `creative ${index + 1}`} for ${group.displayName}`}
                                onChange={(event) => onToggleCreative(group, entry.version.id, event.currentTarget)}
                              />
                              <span><IconCheck size={14} /> {choiceSelected ? 'Selected' : 'Select ad'}</span>
                            </label>
                            <PosterThumbnail
                              key={`${companyId}:${scopeKey}:${catalogEpoch}:${entry.version.id}`}
                              companyId={companyId}
                              scopeKey={scopeKey}
                              cacheScopeKey={mediaScopeKey}
                              catalogEpoch={catalogEpoch}
                              versionId={entry.version.id}
                              index={index}
                              creativeName={entry.package?.displayName || entry.version.headline}
                              onPlay={(opener) => onQuickPlay(entry, opener)}
                              className="bl-setup-video-placeholder"
                            />
                            <div className="bl-setup-creative-copy">
                              <div>Approved choice {index + 1}</div>
                              <h4>{entry.package?.displayName || `Creative ${index + 1}`}</h4>
                              <strong>{entry.version.headline}</strong>
                              <p>{entry.version.primaryText}</p>
                              <span>CTA · {String(entry.version.callToAction || 'LEARN_MORE').replaceAll('_', ' ')}</span>
                              <button type="button" onClick={(event) => onPreview(entry, event.currentTarget)}><IconPlay size={13} /> Preview video &amp; copy</button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                    {collapsible && (
                      <div className="bl-group-browse-bar">
                        <p>{groupExpanded ? count : COLLAPSED_CREATIVE_COUNT} of {count} choices</p>
                        <button
                          type="button"
                          aria-expanded={groupExpanded}
                          aria-controls={gridId}
                          onClick={(event) => toggleExpanded(group, event.currentTarget)}
                        >
                          {groupExpanded ? 'Show fewer' : `View all ${count} creatives`} <span aria-hidden="true">{groupExpanded ? '−' : '+'}</span>
                        </button>
                      </div>
                    )}
                  </fieldset>
                )}
              </article>
            );
          })}
        </div>
      </fieldset>
    </section>
  );
}

export function MetaStep({
  options,
  draft,
  editable,
  accountSelection,
  formSelection,
  formMode = 'existing',
  metaConnection,
  connectionBusy = '',
  recommendedPageResourceId = '',
  recommendedForm = null,
  recommendedConflict = false,
  recommendedBusy = false,
  onChange,
  onFormModeChange = () => {},
  onConnect,
  onCheckAuthorization,
  onRefresh,
  onRecommendedPageChange = () => {},
  onStartRecommendedForm = () => {},
  onRecommendedFormChange = () => {},
  onPreviewRecommendedForm = () => {},
  onConfirmRecommendedForm = () => {},
  onCancelRecommendedForm = () => {},
}) {
  const instantFormSetup = options.instantFormSetup || normalizeInstantFormSetup();
  const policyFormResourceIds = new Set(instantFormSetup.formResourceIds);
  const compatibleCredentialIds = new Set(instantFormSetup.compatibleCredentialIds);
  const creationCredentialIds = new Set(instantFormSetup.creationPages.map(({ credentialId }) => credentialId));
  const recoveryCredentialId = stringValue(instantFormSetup.recovery?.credentialId);
  const accountCredentialAllowed = (credentialId) => (
    instantFormSetupUsesExistingForm(instantFormSetup)
      ? compatibleCredentialIds.has(credentialId)
      : instantFormSetup.state === 'creation_available'
        ? creationCredentialIds.has(credentialId)
        : instantFormSetup.state === 'recovery_available'
          ? credentialId === recoveryCredentialId
          : false
  );
  const accounts = options.metaConnections.flatMap((connection) => connection.adAccounts.map((account) => ({
    connection,
    account,
    policyCompatible: accountCredentialAllowed(connection.credentialId),
  })));
  const formConnection = accountSelection.connection || formSelection.connection;
  const forms = (
    accountSelection.connection
    || formSelection.connection
  )?.instantForms.filter((form) => (
    policyFormResourceIds.has(form.resourceId)
    && compatibleCredentialIds.has(formConnection?.credentialId)
  )) || [];
  const selectedPage = formSelection.form?.owningPage || null;
  const hasSelectedForm = Boolean(
    instantFormSetupUsesExistingForm(instantFormSetup)
    && formSelection.form
    && policyFormResourceIds.has(formSelection.form.resourceId)
    && compatibleCredentialIds.has(formSelection.connection?.credentialId),
  );
  const recommendedCandidates = recommendedInstantFormCandidates(options, accountSelection);
  const identities = (formSelection.connection?.instagramIdentities || []).filter((identity) => (
    sameOwningPage(identity.owningPage, selectedPage)
  ));
  const connectionPayload = metaConnection?.payload || { connection: null, capabilities: {} };
  const connectionState = curatedMetaConnectionState(connectionPayload);
  const authorizationPresentation = curatedMetaAuthorizationPresentation(connectionPayload);
  const authorizationUsable = curatedMetaAuthorizationUsable(connectionPayload);
  const connectionPresentation = curatedMetaConnectionPresentation(connectionPayload);
  const retryAfterSeconds = curatedMetaRetryAfterSeconds(connectionPayload);
  const canManageConnection = metaConnection?.status === 'ready'
    && connectionPayload.capabilities?.canManage === true;
  const canRefreshConnection = canManageConnection
    && connectionPayload.capabilities?.canRefresh === true;
  const canCheckConnection = canManageConnection
    && connectionPayload.capabilities?.canCheckAuthorization === true;
  const canManageRecommendedForm = canManageConnection
    && authorizationUsable
    && instantFormSetup.canManage;
  const canEnterCreationMode = instantFormSetupOffersCreation(instantFormSetup)
    && canManageRecommendedForm
    && recommendedCandidates.length > 0;
  const effectiveFormMode = canEnterCreationMode && !hasSelectedForm
    ? 'create'
    : 'existing';
  const selectedRecommendedCandidate = effectiveFormMode === 'create'
    ? findRecommendedInstantFormCandidate(
        options,
        accountSelection,
        recommendedPageResourceId,
      ) || (recommendedCandidates.length === 1 ? recommendedCandidates[0] : null)
    : null;
  const selectedRecommendedPage = selectedRecommendedCandidate?.page || null;
  const isRecommendedRecovery = selectedRecommendedCandidate?.mode === 'recover';
  const recommendedPreviewValid = recommendedForm?.status === 'review'
    && validRecommendedInstantFormPreview(recommendedForm.preview, {
      privacyPolicyUrl: recommendedForm.privacyPolicyUrl,
      followUpUrl: recommendedForm.followUpUrl,
    });
  const hasRecommendedRecovery = instantFormSetup.state === 'recovery_available'
    && !hasSelectedForm
    && canManageRecommendedForm
    && recommendedCandidates.some((candidate) => candidate.mode === 'recover');
  const recommendedCreationNeedsManager = !hasSelectedForm
    && metaConnection?.status === 'ready'
    && authorizationUsable
    && (
      instantFormSetup.managerActionRequired
      || (instantFormSetup.canManage && connectionPayload.capabilities?.canManage === false)
    )
    && ['creation_available', 'recovery_available'].includes(instantFormSetup.state);
  const hasHealthyUsableResourcePair = options.metaConnections.some((connection) => (
    connection.connectionState === 'ready'
    && !connection.stale
    && !connection.refreshRequired
    && connection.adAccounts.some((account) => account.selectable && accountCredentialAllowed(connection.credentialId))
    && (instantFormSetupUsesExistingForm(instantFormSetup)
      ? connection.instantForms.some((form) => (
          policyFormResourceIds.has(form.resourceId)
          && (form.selectable || form.selectableForVerification)
        ))
      : instantFormSetup.state === 'creation_available'
        && creationCredentialIds.has(connection.credentialId))
  ));
  const catalogNeedsRefresh = options.metaConnections.some((connection) => (
    connection.refreshRequired || connection.stale
  ));
  const connectionStatusUnavailable = metaConnection?.status === 'error'
    && !hasHealthyUsableResourcePair;
  const connectionHasExplicitRecoveryState = metaConnection?.status === 'ready'
    && [
      'unknown',
      'checking',
      'expiring',
      'temporarily_unavailable',
      'reauth_required',
      'disconnected',
      'refresh_required',
      'refresh_in_progress',
      'waiting_on_meta',
      'sync_failed',
      'unhealthy',
    ].includes(connectionState);
  const recommendedRecoveryReplacesRefresh = hasRecommendedRecovery
    && ['connected', 'refresh_required', 'refresh_in_progress', 'waiting_on_meta'].includes(connectionState);
  const setupExplainsMissingResourcePair = connectionState === 'connected'
    && ['page_assignment_required', 'blocked'].includes(instantFormSetup.state);
  const showConnectionRecovery = !connectionStatusUnavailable && (
    (!hasHealthyUsableResourcePair && !hasRecommendedRecovery && !setupExplainsMissingResourcePair)
    || (connectionHasExplicitRecoveryState && !recommendedRecoveryReplacesRefresh)
  );
  const showConnectionStatusError = metaConnection?.status === 'error';
  const emptyStateTitle = connectionState === 'waiting_on_meta'
    ? connectionPresentation.label
    : connectionState === 'refresh_in_progress'
    ? 'Connected — updating resources'
    : connectionState === 'sync_failed'
      ? 'Meta sync needs attention'
    : connectionState === 'refresh_required' || (connectionState === 'connected' && catalogNeedsRefresh)
      ? 'Connected — updating resources'
      : [
          'missing',
          'unknown',
          'checking',
          'expiring',
          'temporarily_unavailable',
          'reauth_required',
          'disconnected',
          'unhealthy',
        ].includes(connectionState)
        ? connectionPresentation.label
        : 'No Meta ad account is ready';
  let emptyStateBody;
  if (connectionState === 'waiting_on_meta') {
    // OGC-483: one presentation for "waiting on Meta" everywhere it appears.
    emptyStateBody = connectionPresentation.description;
  } else if (connectionState === 'refresh_in_progress') {
    emptyStateBody = `BeyondLeads is updating this company’s Meta resources in the background. This view will update automatically within about ${retryAfterSeconds} seconds.`;
  } else if (connectionState === 'sync_failed') {
    emptyStateBody = canManageConnection
      ? 'The background update failed safely. Retry sync without reconnecting Meta.'
      : 'Ask a company owner or admin to retry Meta synchronization.';
  } else if (connectionState === 'refresh_required' || (connectionState === 'connected' && catalogNeedsRefresh)) {
    emptyStateBody = canManageConnection
      ? 'BeyondLeads will update this company’s current ad accounts and Instant Forms automatically.'
      : 'Ask a company owner or admin to refresh Meta resources.';
  } else if (canManageConnection) {
    emptyStateBody = connectionPresentation.description;
  } else if (connectionState === 'missing') {
    emptyStateBody = 'Ask a company owner or admin to connect Meta.';
  } else if (['reauth_required', 'disconnected'].includes(connectionState)) {
    emptyStateBody = 'Ask a company owner or admin to reconnect Meta.';
  } else if (connectionState === 'expiring') {
    emptyStateBody = 'Ask a company owner or admin to renew Meta authorization before it expires.';
  } else if (connectionState === 'checking') {
    emptyStateBody = 'A company owner or admin is checking Meta authorization.';
  } else if (['unknown', 'temporarily_unavailable', 'unhealthy'].includes(connectionState)) {
    emptyStateBody = 'Ask a company owner or admin to check Meta authorization.';
  } else {
    emptyStateBody = 'Meta is connected, but no eligible ad account and Instant Form are ready.';
  }
  const setupGuidance = connectionStatusUnavailable
    ? null
    : instantFormSetup.state === 'page_assignment_required'
    ? {
        title: 'Assign a Facebook Page before creating an Instant Form',
        body: instantFormSetup.canManage
          ? 'Open Company settings and explicitly assign one of this company’s available Facebook Pages, then return here and refresh Meta resources.'
          : 'Ask a company owner or admin to assign a Facebook Page to this company in Company settings.',
      }
    : instantFormSetup.state === 'blocked'
      ? {
          title: 'Instant Form setup is unavailable',
          body: canRefreshConnection
            ? 'Refresh Meta resources. If this state continues, contact support before changing campaign resources.'
            : 'Ask a company owner or admin to refresh Meta resources or contact support.',
        }
      : instantFormSetup.state === 'existing_repair_required'
        ? {
            title: 'An existing Instant Form needs verification',
            body: canRefreshConnection
              ? 'Select the company’s existing Form below. BeyondLeads will verify it before provider use; refresh Meta resources if it remains unavailable.'
              : 'Select the company’s existing Form below and ask an owner or admin to refresh Meta resources if it remains unavailable.',
          }
        : null;
  return (
    <section
      className="bl-campaign-panel"
      aria-labelledby="meta-step-title"
      data-connection-state={connectionState}
    >
      <StepHeading
        kicker="Step 2 of 5"
        title="Connect the approved Meta resources"
        body="BeyondLeads uses the company-wide Instant Form policy. When an existing Form is available, its Facebook Page is always derived automatically."
      />
      <fieldset className="bl-campaign-fieldset">
        <legend id="meta-step-title">Meta lead campaign resources</legend>
        {(showConnectionRecovery || showConnectionStatusError || (canManageConnection && metaCredentialId(connectionPayload.connection))) && (
          <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
            {showConnectionRecovery && (
              <SetupNotice compact tone="warning" title={emptyStateTitle} body={emptyStateBody} />
            )}
            {(showConnectionRecovery || (canManageConnection && metaCredentialId(connectionPayload.connection))) && (
                <CuratedMetaConnectionActions
                  payload={connectionPayload}
                  canManage={canManageConnection}
                  canRefresh={canRefreshConnection}
                  canCheckAuthorization={canCheckConnection}
                  busy={connectionBusy}
                  onConnect={onConnect}
                  onCheckAuthorization={onCheckAuthorization}
                  onRefresh={onRefresh}
                />
            )}
            {showConnectionStatusError && (
              <SetupNotice compact tone="warning" role="alert" title="Meta connection status is unavailable" body={safeFailureMessage(metaConnection.error, 'integration')} />
            )}
          </div>
        )}
        {hasRecommendedRecovery && (
          <SetupNotice
            compact
            tone="warning"
            title="Recommended Form request can resume"
            body="Review the exact recovery Facebook Page, then choose Resume previous Form setup. BeyondLeads will reconcile the original dispatched request first and will not repeat the Instant Form creation write."
          />
        )}
        {recommendedCreationNeedsManager && (
          <SetupNotice
            compact
            tone="warning"
            title="Instant Form setup needs an owner or admin"
            body={instantFormSetup.state === 'recovery_available'
              ? 'Ask a company owner or admin to resume the previous Form setup.'
              : 'Ask a company owner or admin to create the recommended Instant Form on an assigned Facebook Page.'}
          />
        )}
        {setupGuidance && (
          <SetupNotice
            compact
            tone="warning"
            title={setupGuidance.title}
            body={setupGuidance.body}
          />
        )}
        <div className="bl-form-grid">
          <label htmlFor="meta-ad-account">
            <span>Ad account <b aria-hidden="true">*</b></span>
            <Select
              className="bl-select-block"
              id="meta-ad-account"
              value={draft.meta.adAccountResourceId}
              disabled={!editable || !accounts.length}
              onChange={(value) => onChange('adAccountResourceId', value)}
              required
              options={[
                { value: '', label: 'Select an ad account' },
                ...accounts.map(({ connection, account, policyCompatible }) => ({
                  value: account.resourceId,
                  label: `${account.label}${account.providerId ? ` · ${account.providerId}` : ''}${account.currency ? ` · ${account.currency}` : ''}${connection.stale ? ' · Updating resources' : ''}`,
                  disabled: !account.selectable || !policyCompatible,
                })),
              ]}
            />
          </label>
          {instantFormSetupUsesExistingForm(instantFormSetup) && (
            <label htmlFor="meta-instant-form">
              <span>Instant Form <b aria-hidden="true">*</b></span>
              <Select
                className="bl-select-block"
                id="meta-instant-form"
                value={draft.meta.instantFormResourceId}
                disabled={!editable || !accountSelection.account || !forms.length}
                onChange={(value) => onChange('instantFormResourceId', value)}
                required
                options={[
                  { value: '', label: 'Select an Instant Form' },
                  ...forms.map((form) => ({
                    value: form.resourceId,
                    label: `${form.label}${form.providerId ? ` · ${form.providerId}` : ''}`,
                    disabled: !form.selectable && !form.selectableForVerification,
                  })),
                ]}
              />
              <small>Required for webhook lead ingestion.</small>
            </label>
          )}
          {effectiveFormMode === 'create'
            && instantFormSetup.state === 'creation_available'
            && recommendedCandidates.length > 1 && (
            <label htmlFor="meta-recommended-page">
              <span>Page for new Instant Form <b aria-hidden="true">*</b></span>
              <Select
                className="bl-select-block"
                id="meta-recommended-page"
                aria-label="Page for new Instant Form"
                aria-describedby="meta-recommended-page-help"
                value={recommendedPageResourceId}
                disabled={!editable || recommendedBusy}
                onChange={onRecommendedPageChange}
                required
                options={[
                  { value: '', label: 'Select a Page' },
                  ...recommendedCandidates.map((candidate) => ({
                    value: candidate.page.resourceId,
                    label: `${candidate.page.label}${candidate.page.providerId ? ` · ${candidate.page.providerId}` : ''}${candidate.mode === 'recover' ? ' · Verification recovery' : ''}`,
                  })),
                ]}
              />
              <small id="meta-recommended-page-help">
                Only company-assigned Pages authorized by the server for this Form request are shown.
              </small>
            </label>
          )}
          {effectiveFormMode === 'create' && recommendedCandidates.length === 1 && (
            <div className="bl-derived-resource" aria-live="polite">
              <span>{isRecommendedRecovery ? 'Recovery Facebook Page' : 'Assigned Facebook Page'}</span>
              <strong>{selectedRecommendedPage?.label || 'Facebook Page unavailable'}</strong>
              <small>
                {selectedRecommendedPage?.providerId ? `Facebook Page ID ${selectedRecommendedPage.providerId} · ` : ''}
                {isRecommendedRecovery
                  ? 'This exact Page is bound to the dispatched request and cannot be changed.'
                  : 'This is the only company-assigned Page authorized for Instant Form creation.'}
              </small>
            </div>
          )}
          {instantFormSetupUsesExistingForm(instantFormSetup) && (
            <>
              <label htmlFor="meta-instagram">
                <span>Instagram account <em>Optional</em></span>
                <Select
                  className="bl-select-block"
                  id="meta-instagram"
                  value={draft.meta.instagramIdentityResourceId}
                  disabled={!editable || !formSelection.form}
                  onChange={(value) => onChange('instagramIdentityResourceId', value)}
                  options={[
                    { value: '', label: 'Use Facebook Page identity only' },
                    ...identities.map((identity) => ({
                      value: identity.resourceId,
                      label: identity.label,
                      disabled: !identity.selectable && !identity.selectableForVerification,
                    })),
                  ]}
                />
              </label>
              <div className="bl-derived-resource" aria-live="polite">
                <span>Derived Facebook Page</span>
                <strong>{selectedPage?.label || 'Select an Instant Form'}</strong>
                <small>
                  {selectedPage?.providerId ? `Facebook Page ID ${selectedPage.providerId} · ` : ''}
                  The server verifies ownership and derives this Page.
                </small>
              </div>
            </>
          )}
        </div>
        {selectedRecommendedPage && !recommendedForm && canManageRecommendedForm && (
          <div className="bl-recommended-form-action">
            <div>
              <strong>{selectedRecommendedPage.label}</strong>
              <small>Facebook Page ID {selectedRecommendedPage.providerId || 'verified by Meta'}</small>
            </div>
            <button
              type="button"
              disabled={!editable || recommendedBusy || recommendedConflict}
              onClick={onStartRecommendedForm}
            >
              {isRecommendedRecovery ? 'Resume previous Form setup' : 'Create recommended Instant Form'}
            </button>
          </div>
        )}
        {selectedRecommendedPage && recommendedConflict && canManageConnection && (
          <div className="bl-recommended-form-conflict" role="alert">
            <p>
              {safeFailureMessage({ code: 'meta_recommended_form_active_form_conflict' }, 'curated-campaign')}
            </p>
            {canRefreshConnection && (
              <button
                type="button"
                className="is-secondary"
                disabled={Boolean(connectionBusy) || recommendedBusy}
                onClick={onRefresh}
              >
                Refresh Meta resources
              </button>
            )}
          </div>
        )}
        {selectedRecommendedPage && recommendedForm?.status === 'edit' && canManageRecommendedForm && (
          <section className="bl-recommended-form" aria-labelledby="recommended-form-create-title">
            <div className="bl-recommended-form-heading">
              <div>
                <span>Verified Facebook Page</span>
                <h3 id="recommended-form-create-title">
                  {isRecommendedRecovery ? 'Resume previous Form setup' : 'Create recommended Instant Form'}
                </h3>
                <p>{selectedRecommendedPage.label} · {selectedRecommendedPage.providerId}</p>
              </div>
            </div>
            <div className="bl-recommended-form-fields">
              <label htmlFor="recommended-form-privacy-url">
                <span>Privacy policy URL</span>
                <input
                  id="recommended-form-privacy-url"
                  type="url"
                  value={recommendedForm.privacyPolicyUrl || ''}
                  disabled={!editable || recommendedBusy}
                  onChange={(event) => onRecommendedFormChange('privacyPolicyUrl', event.target.value)}
                  placeholder="https://example.com/privacy"
                  required
                />
              </label>
              <label htmlFor="recommended-form-follow-up-url">
                <span>Follow-up URL</span>
                <input
                  id="recommended-form-follow-up-url"
                  type="url"
                  value={recommendedForm.followUpUrl || ''}
                  disabled={!editable || recommendedBusy}
                  onChange={(event) => onRecommendedFormChange('followUpUrl', event.target.value)}
                  placeholder="https://example.com/thank-you"
                  required
                />
              </label>
            </div>
            <p className="bl-recommended-form-note">
              {isRecommendedRecovery
                ? 'Re-enter and review the same URLs. BeyondLeads will reconcile the original request before any Form-creation write and will not repeat that write.'
                : 'Review the server-owned name, questions, consent, and thank-you content before anything is created in Meta.'}
            </p>
            <div className="bl-recommended-form-actions">
              <button type="button" className="is-secondary" disabled={recommendedBusy} onClick={onCancelRecommendedForm}>Cancel</button>
              <button
                type="button"
                disabled={
                  !editable
                  || recommendedBusy
                  || recommendedConflict
                  || !stringValue(recommendedForm.privacyPolicyUrl)
                  || !stringValue(recommendedForm.followUpUrl)
                }
                onClick={onPreviewRecommendedForm}
              >
                Review form details
              </button>
            </div>
          </section>
        )}
        {selectedRecommendedPage && recommendedForm?.status === 'review' && canManageRecommendedForm && (
          <RecommendedInstantFormReview
            page={selectedRecommendedPage}
            recommendedForm={recommendedForm}
            recovery={isRecommendedRecovery}
            busy={recommendedBusy}
            conflict={recommendedConflict}
            canConfirm={recommendedPreviewValid}
            onCancel={onCancelRecommendedForm}
            onConfirm={onConfirmRecommendedForm}
          />
        )}
      </fieldset>
    </section>
  );
}

function RecommendedInstantFormReview({
  page,
  recommendedForm,
  recovery = false,
  busy,
  conflict = false,
  canConfirm,
  onCancel,
  onConfirm,
}) {
  const preview = recommendedForm?.preview || {};
  const questions = Array.isArray(preview.questions) ? preview.questions : [];
  const privacyPolicyUrl = safeHttpsExternalUrl(preview.privacyPolicy?.url);
  const followUpUrl = safeHttpsExternalUrl(preview.thankYou?.followUpUrl);
  return (
    <section className="bl-recommended-form" aria-labelledby="recommended-form-review-title">
      <div className="bl-recommended-form-heading">
        <div>
          <span>Confirmation required</span>
          <h3 id="recommended-form-review-title">
            {recovery ? 'Review previous Form setup' : 'Review recommended Instant Form'}
          </h3>
          <p>
            {recovery
              ? 'Resume previous Form setup safely reconciles the original request and will not repeat the Instant Form creation write.'
              : 'Nothing is written to Meta until you choose Create & use.'}
          </p>
        </div>
      </div>
      <dl className="bl-recommended-form-review">
        <div>
          <dt>Developer app</dt>
          <dd>{stringValue(preview.developerAppName) || 'Beyond Leads'}</dd>
        </div>
        <div>
          <dt>Facebook Page</dt>
          <dd>{page.label} · {page.providerId}</dd>
        </div>
        <div>
          <dt>Form name</dt>
          <dd>{stringValue(preview.formName) || 'Recommended renovation leads form'}</dd>
        </div>
        <div>
          <dt>Locale</dt>
          <dd>{stringValue(preview.locale) || 'EN_US'}</dd>
        </div>
        <div>
          <dt>Questions</dt>
          <dd>
            <ul>
              {questions.map((question, index) => (
                <li key={stringValue(question?.key) || `question-${index + 1}`}>
                  {stringValue(question?.label) || 'Contact detail'}
                  {Array.isArray(question?.options) && (
                    <ul>
                      {question.options.map((option, optionIndex) => (
                        <li key={stringValue(option?.key) || `option-${optionIndex + 1}`}>
                          {stringValue(option?.label)}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </dd>
        </div>
        <div>
          <dt>Privacy policy</dt>
          <dd>
            {privacyPolicyUrl
              ? (
                <>
                  <a href={privacyPolicyUrl} target="_blank" rel="noreferrer">
                    {stringValue(preview.privacyPolicy?.linkText) || 'View privacy policy'}
                  </a>
                  <span>{privacyPolicyUrl}</span>
                </>
                )
              : <span>Privacy policy URL unavailable</span>}
          </dd>
        </div>
        <div>
          <dt>Consent &amp; disclaimer</dt>
          <dd>
            <strong>{stringValue(preview.consent?.title) || 'Consent title unavailable'}</strong>
            <span>{stringValue(preview.consent?.body) || 'Consent details unavailable'}</span>
            <span>{stringValue(preview.consent?.checkbox?.text) || 'Consent checkbox unavailable'}</span>
            <small>
              {preview.consent?.checkbox?.required === true ? 'Required' : 'Optional'}
              {' · '}
              {preview.consent?.checkbox?.checkedByDefault === false ? 'Not preselected' : 'Preselected'}
            </small>
          </dd>
        </div>
        <div>
          <dt>Thank-you</dt>
          <dd>
            <strong>{stringValue(preview.thankYou?.headline) || 'Thank-you headline'}</strong>
            <span>{stringValue(preview.thankYou?.body)}</span>
            {followUpUrl
              ? (
                <>
                  <a href={followUpUrl} target="_blank" rel="noreferrer">
                    {stringValue(preview.thankYou?.buttonText) || 'Visit website'}
                  </a>
                  <span>{followUpUrl}</span>
                </>
                )
              : <span>Follow-up URL unavailable</span>}
          </dd>
        </div>
      </dl>
      <div className="bl-recommended-form-actions">
        <button type="button" className="is-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
        <button type="button" disabled={busy || !canConfirm || conflict} onClick={onConfirm}>
          {recovery ? 'Resume previous Form setup' : 'Create & use'}
        </button>
      </div>
    </section>
  );
}

export function CityPicker({ id, label, kind, country, onSearch, locations, selectedKeys, otherKeys, disabled, onAdd, onRemove }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = `${id}-results`;
  const [search, setSearch] = useState({ query: '', status: 'idle', locations: [] });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    if (disabled || query.trim().length < 2) {
      setSearch({ query, status: 'idle', locations: [] });
      return () => controller.abort();
    }
    setSearch({ query, status: 'loading', locations: [] });
    const timer = setTimeout(async () => {
      try {
        const result = await onSearch(country, query.trim(), controller.signal);
        if (!controller.signal.aborted) setSearch({ query, status: 'ready', locations: result });
      } catch (error) {
        if (controller.signal.aborted) return;
        const message = error?.code === 'curated_city_connection_required'
          ? 'Your company Meta connection needs attention before you can search for cities.'
          : error?.status === 429
            ? 'City search is busy. Wait a minute and retry.'
            : 'City search is temporarily unavailable. Your saved selections are unchanged.';
        setSearch({ query, status: 'error', message, locations: [] });
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, country, disabled, onSearch, retry]);
  const available = search.query === query && search.status === 'ready'
    ? search.locations.filter((location) => !selectedKeys.includes(location.key) && !otherKeys.includes(location.key))
    : [];
  const selected = selectedKeys.map((key) => locations.find((location) => location.key === key) || { key, label: 'Unavailable city' });

  function choose(location) {
    if (onAdd(kind, location.key, location)) {
      setQuery('');
      setOpen(false);
      setActiveIndex(0);
    }
  }

  function onKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, Math.max(available.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && open && available[activeIndex]) {
      event.preventDefault();
      choose(available[activeIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="bl-city-picker">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open && available.length > 0}
        aria-controls={listId}
        aria-activedescendant={open && available[activeIndex] ? `${listId}-${activeIndex}` : undefined}
        autoComplete="off"
        value={query}
        disabled={disabled}
        placeholder="Start typing a city"
        maxLength={100}
        aria-describedby={`${id}-status`}
        onBlur={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onChange={(event) => { setQuery(event.target.value); setOpen(true); setActiveIndex(0); }}
        onKeyDown={onKeyDown}
      />
      {open && available.length > 0 && (
        <ul id={listId} role="listbox" className="bl-city-results">
          {available.map((location, index) => (
            <li
              key={location.key}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(location)}
            >
              {location.label}
            </li>
          ))}
        </ul>
      )}
      <div id={`${id}-status`} role="status" aria-live="polite">
        {query.trim().length < 2 ? 'Type at least 2 characters to search Meta cities.'
          : search.status === 'loading' ? 'Searching cities…'
            : search.status === 'error' ? search.message
              : search.status === 'ready' && !available.length ? 'No matching unselected cities. Try a different city name.' : ''}
      </div>
      {search.status === 'error' && <button type="button" disabled={disabled} onClick={() => setRetry((value) => value + 1)}>Retry city search</button>}
      <div className="bl-city-chips" aria-live="polite">
        {selected.map((location) => (
          <span key={location.key} className={location.label === 'Unavailable city' ? 'is-stale' : ''}>
            {location.label}
            <button type="button" disabled={disabled} aria-label={`Remove ${location.label} from ${label.toLowerCase()}`} onClick={() => onRemove(kind, location.key)}>×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

function TargetingStep({ options, draft, editable, onCountry, onSearchCities, onAddCity, onRemoveCity, onRadius }) {
  const country = draft.targeting.country;
  const bounds = options.radiusBounds[country];
  const locations = options.locations[country];
  return (
    <section className="bl-campaign-panel" aria-labelledby="targeting-step-title">
      <StepHeading
        kicker="Step 3 of 5"
        title="Define the local service area"
        body="Search cities available in Meta and apply one radius around each included city."
      />
      <fieldset className="bl-campaign-fieldset">
        <legend id="targeting-step-title">Country and city targeting</legend>
        <div className="bl-country-choice">
          {[
            ['CA', 'Canada'],
            ['US', 'United States'],
          ].map(([code, label]) => (
            <label key={code}>
              <input type="radio" name="target-country" value={code} checked={country === code} disabled={!editable} onChange={() => onCountry(code)} />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <div className="bl-targeting-grid">
          <CityPicker
            key={`included-${country}`}
            country={country}
            onSearch={onSearchCities}
            id="included-city-search"
            label="Included cities"
            kind="included"
            locations={locations}
            selectedKeys={draft.targeting.includedCityKeys}
            otherKeys={draft.targeting.excludedCityKeys}
            disabled={!editable}
            onAdd={onAddCity}
            onRemove={onRemoveCity}
          />
          <CityPicker
            key={`excluded-${country}`}
            country={country}
            onSearch={onSearchCities}
            id="excluded-city-search"
            label="Excluded cities"
            kind="excluded"
            locations={locations}
            selectedKeys={draft.targeting.excludedCityKeys}
            otherKeys={draft.targeting.includedCityKeys}
            disabled={!editable}
            onAdd={onAddCity}
            onRemove={onRemoveCity}
          />
        </div>
        <label className="bl-radius-field" htmlFor="target-radius">
          <span>Radius around every included city ({bounds.unit})</span>
          <input
            id="target-radius"
            type="number"
            inputMode="decimal"
            min={bounds.min}
            max={bounds.max}
            step="1"
            value={draft.targeting.radius.value}
            disabled={!editable}
            onChange={(event) => onRadius(event.target.value)}
          />
          <small>Allowed range: {bounds.min}–{bounds.max} {bounds.unit}. The country-appropriate unit is fixed.</small>
        </label>
      </fieldset>
    </section>
  );
}

function BudgetStep({
  draft,
  currency,
  dailyBudget,
  canEdit,
  lockedReason,
  providerMinimum,
  providerMinimumUnavailable = false,
  onChange,
}) {
  const providerMinimumDescriptionId = providerMinimum || providerMinimumUnavailable
    ? 'monthly-budget-provider-minimum'
    : undefined;
  return (
    <section className="bl-campaign-panel" aria-labelledby="budget-step-title">
      <StepHeading
        kicker="Step 4 of 5"
        title="Set one campaign budget"
        body="The monthly amount funds the campaign. It is never multiplied across renovation types or ads."
      />
      <fieldset className="bl-campaign-fieldset">
        <legend id="budget-step-title">Campaign budget in ad-account currency</legend>
        <div className="bl-budget-layout">
          <label htmlFor="monthly-budget">
            <span>Monthly campaign budget ({currency || 'select an ad account'})</span>
            <div className="bl-money-input">
              <span>{currency || '—'}</span>
              <input
                id="monthly-budget"
                type="number"
                inputMode="decimal"
                min={providerMinimum?.minimumMonthlyMajor || '0'}
                step="0.01"
                value={draft.budget.monthlyMajor}
                disabled={!canEdit}
                aria-describedby={providerMinimumDescriptionId}
                onChange={(event) => onChange(event.target.value)}
              />
            </div>
            {providerMinimum && (
              <small id={providerMinimumDescriptionId}>
                Meta account minimum: {displayMinorCurrency(
                  providerMinimum.minimumDailyMinor,
                  providerMinimum.currency,
                )} per day. Enter at least {displayCurrency(
                  providerMinimum.minimumMonthlyMajor,
                  providerMinimum.currency,
                )} monthly.
              </small>
            )}
            {providerMinimumUnavailable && (
              <small id="monthly-budget-provider-minimum">
                Refresh this campaign before submitting because the Meta account minimum could not be verified.
              </small>
            )}
            {!canEdit && <small>{lockedReason || 'Only a company owner or admin can set or change campaign budget.'}</small>}
          </label>
          <div className="bl-daily-budget" aria-live="polite">
            <span>Derived Meta daily budget</span>
            <strong>{displayMinorCurrency(dailyBudget.minor, dailyBudget.currency || currency)}</strong>
            <small>
              {dailyBudget.minor === null
                ? 'Save the draft to apply server-owned rounding and minimum rules.'
                : `${dailyBudget.minor} minor units${dailyBudget.rounding ? ` · ${displayBudgetRounding(dailyBudget.rounding)}` : ''}${dailyBudget.minimumMinor !== null ? ` · minimum ${dailyBudget.minimumMinor} minor units` : ''}`}
            </small>
          </div>
        </div>
      </fieldset>
    </section>
  );
}

function locationLabels(options, country, keys) {
  return keys.map((key) => options.locations[country].find((location) => location.key === key)?.label || 'Unavailable city');
}

export function ReviewStep({
  options,
  draft,
  findings,
  serverBlockers,
  status,
  accountSelection,
  formSelection,
  dailyBudget,
  reviewSnapshot,
  recordedDestination = null,
  hasPreflightRecovery = false,
  onPreview,
}) {
  const selectedGroups = options.catalog.filter((group) => draft.selectedTypeIds.includes(group.id));
  // Sealed reviews (including an attempt that is publishing) always render the
  // immutable snapshot, never the live catalog.
  const sealed = ['awaiting_approval', 'approved', 'publishing'].includes(status) ? reviewSnapshot : null;
  const sealedTypeNames = sealed ? [...new Set(sealed.ads.map((ad) => ad.renovationTypeName))] : [];
  const adCount = sealed?.structure.adCount ?? curatedSelectedAdCount(options, draft);
  const country = sealed?.targeting.country || draft.targeting.country;
  const bounds = options.radiusBounds[country];
  const radius = sealed?.targeting.radius || draft.targeting.radius;
  const included = sealed
    ? sealed.targeting.includedCities.map((city) => city.label)
    : locationLabels(options, country, draft.targeting.includedCityKeys);
  const excluded = sealed
    ? sealed.targeting.excludedCities.map((city) => city.label)
    : locationLabels(options, country, draft.targeting.excludedCityKeys);
  const currency = sealed?.budget.currency || stringValue(accountSelection.account?.currency).toUpperCase();
  const monthlyMajor = sealed?.budget.monthlyMajor || draft.budget.monthlyMajor;
  const reviewDailyBudget = sealed ? {
    minor: sealed.budget.dailyMinor,
    currency: sealed.budget.currency,
    rounding: sealed.budget.roundingRule,
    minimumMinor: sealed.budget.minimumDailyMinor,
  } : dailyBudget;
  const accountLabel = sealed?.meta.adAccountLabel || recordedDestination?.adAccount?.label || accountSelection.account?.label;
  const formLabel = sealed?.meta.instantFormLabel || recordedDestination?.instantForm?.label || formSelection.form?.label;
  const pageLabel = sealed?.meta.pageLabel || recordedDestination?.page?.label || formSelection.form?.owningPage?.label;
  return (
    <section className="bl-campaign-panel" aria-labelledby="review-step-title">
      <StepHeading
        kicker="Step 5 of 5"
        title="Review your campaign"
        body="Creating seals these exact settings and immediately creates the campaign, ad set, and ads in Meta as paused. They cannot deliver or spend until an owner or admin activates them."
      />
      <h3 id="review-step-title" className="bl-review-summary-title">Campaign structure</h3>
      {sealed && (
        <SetupNotice
          compact
          tone="success"
          title="Saved campaign review"
          body={`Showing the immutable inputs from configuration revision ${sealed.campaign.configurationRevision}. Live catalog changes cannot rewrite this review.`}
        />
      )}
      <div className="bl-structure-summary" aria-label="Meta campaign structure">
        <div><strong>{sealed?.structure.campaignCount ?? 1}</strong><span>Campaign</span></div>
        <div><strong>{sealed?.structure.adSetCount ?? 1}</strong><span>Ad set</span></div>
        <div><strong>{adCount}</strong><span>Ads</span></div>
        <div className="is-paused"><strong>{sealed?.structure.initialStatus || 'PAUSED'}</strong><span>On create</span></div>
      </div>

      {(findings.length > 0 || serverBlockers.length > 0) && (
        <div className="bl-review-findings" role="alert" tabIndex="-1">
          <strong>{sealed ? 'Campaign creation needs attention' : 'Resolve these items before creating your campaign'}</strong>
          <ul>
            {findings.map((finding) => <li key={`${finding.step}:${finding.field}`}>{finding.message}</li>)}
            {serverBlockers.map((blocker) => <li key={blocker.code}>{safeFailureMessage({ code: blocker.code }, 'curated-campaign')}</li>)}
          </ul>
        </div>
      )}

      <div className="bl-review-grid">
        <ReviewCard title="Published creative">
          <strong>{sealed ? sealedTypeNames.length : selectedGroups.length} renovation type{(sealed ? sealedTypeNames.length : selectedGroups.length) === 1 ? '' : 's'} · {adCount} {adCount === 1 ? 'ad' : 'ads'}</strong>
          {sealed ? (
            <ul className="bl-sealed-ad-list">
              {sealed.ads.map((ad) => (
                <li key={ad.creativeVersionId}>
                  <span>{ad.renovationTypeName} · {ad.packageName}</span>
                  <strong>{ad.headline}</strong>
                  <p>{ad.primaryText}</p>
                  <small>CTA · {ad.callToAction.replaceAll('_', ' ')}</small>
                  <button
                    type="button"
                    onClick={(event) => onPreview?.({
                      package: { displayName: ad.packageName },
                      version: {
                        id: ad.creativeVersionId,
                        headline: ad.headline,
                        primaryText: ad.primaryText,
                        callToAction: ad.callToAction,
                      },
                    }, event.currentTarget)}
                  >
                    <IconPlay size={13} /> Preview sealed ad
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="bl-review-selected-types">
              {selectedGroups.map((group) => {
                const selectedChoices = curatedSelectedChoices(draft, group);
                return (
                  <section key={group.id}>
                    <div className="bl-review-type-heading">
                      <strong>{group.displayName}</strong>
                      <span>{selectedChoices.length} {selectedChoices.length === 1 ? 'ad' : 'ads'} selected</span>
                    </div>
                    {selectedChoices.length > 0 && (
                      <ul className="bl-selected-preview-list">
                        {selectedChoices.map((entry) => {
                          const title = creativeTitle(entry, group.choices.indexOf(entry));
                          return (
                            <li key={entry.version.id}>
                              <div>
                                <strong>{title}</strong>
                                <small>Published · {String(entry.version.callToAction || 'LEARN_MORE').replaceAll('_', ' ').toLowerCase().replace(/^./, (letter) => letter.toUpperCase())}</small>
                              </div>
                              <button
                                type="button"
                                aria-label={`Preview selected ${group.displayName} — ${title}`}
                                onClick={(event) => onPreview?.(entry, event.currentTarget)}
                              >
                                Preview
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </ReviewCard>
        <ReviewCard title="Meta resources">
          <strong>{accountLabel || 'Ad account required'}</strong>
          <p>{formLabel || 'Instant Form required'}</p>
          <p>Page · {pageLabel || 'Derived after Form selection'}</p>
          {(sealed?.meta.instagramLabel || recordedDestination?.instagramIdentity?.label) && <p>Instagram · {sealed?.meta.instagramLabel || recordedDestination.instagramIdentity.label}</p>}
        </ReviewCard>
        <ReviewCard title="Local targeting">
          <strong>{country === 'CA' ? 'Canada' : 'United States'} · {radius.value} {radius.unit || bounds.unit}</strong>
          <p>Included · {included.join(', ') || 'Required'}</p>
          <p>Excluded · {excluded.join(', ') || 'None'}</p>
        </ReviewCard>
        <ReviewCard title="One campaign budget">
          <strong>{displayCurrency(finiteNumber(monthlyMajor, NaN), currency)} monthly</strong>
          <p>{displayMinorCurrency(reviewDailyBudget.minor, reviewDailyBudget.currency || currency)} daily after server rounding</p>
          <p>No ad-set budget and no multiplication.</p>
        </ReviewCard>
      </div>
      {status === 'awaiting_approval' && <SetupNotice compact title="Ready for review" body="Create campaign in Meta — paused saves this reviewed campaign without enabling delivery or spend." />}
      {status === 'approved' && !hasPreflightRecovery && <SetupNotice compact tone="success" title="Ready to create in Meta" body="Creating this campaign keeps its ads paused. An owner or admin must separately activate it to enable delivery and spend." />}
    </section>
  );
}

function ReviewCard({ title, children }) {
  return <section className="bl-review-card"><span>{title}</span>{children}</section>;
}

export function PreflightRecoveryNotice({
  recovery,
  canEditAndReapprove = false,
  busy = '',
  onEditAndReapprove,
  onRefresh,
}) {
  if (!recovery) return null;
  const knownBudgetFailure = ['available', 'manager_required'].includes(recovery.state)
    && recovery.failure;
  if (!knownBudgetFailure) {
    return (
      <section
        className="bl-setup-notice bl-preflight-recovery is-warning is-compact"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-labelledby="preflight-recovery-title"
      >
        <strong id="preflight-recovery-title">Campaign recovery status unavailable</strong>
        <span>BeyondLeads could not confirm whether it is safe to edit or retry this approved campaign. Refresh the durable campaign status before continuing.</span>
        {typeof onRefresh === 'function' && (
          <div className="bl-preflight-recovery-actions">
            <button
              type="button"
              className="is-secondary"
              disabled={Boolean(busy)}
              onClick={onRefresh}
             aria-busy={busy === 'publish-refresh' ? 'true' : undefined}
            >{busy === 'publish-refresh' ? <><Beacon size="inline" className="is-current" delayMs={0} /> Refreshing campaign status…</> : 'Refresh campaign status'}</button>
          </div>
        )}
      </section>
    );
  }
  const dailyMinimum = displayMinorCurrency(
    recovery.failure.minimumDailyMinor,
    recovery.failure.currency,
  );
  const monthlyMinimum = displayCurrency(
    recovery.failure.minimumMonthlyMajor,
    recovery.failure.currency,
  );
  const managerRequired = recovery.state === 'manager_required' || !canEditAndReapprove;
  return (
    <section
      className="bl-setup-notice bl-preflight-recovery is-warning is-compact"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-labelledby="preflight-recovery-title"
    >
      <strong id="preflight-recovery-title">Campaign budget needs attention</strong>
      <span>Meta requires at least {dailyMinimum} per day for this ad account. Enter at least {monthlyMinimum} monthly.</span>
      <span>No Meta campaign, ad set, or ads were created.</span>
      <span>Your original approval and failed creation attempt will remain in campaign history.</span>
      {managerRequired && <span>Ask a company owner or admin to correct the campaign budget, then review it again.</span>}
      {!managerRequired && typeof onEditAndReapprove === 'function' && (
        <div className="bl-preflight-recovery-actions">
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={onEditAndReapprove}
          >{busy === 'preflight-recovery' ? 'Opening draft…' : 'Edit campaign'}</button>
        </div>
      )}
    </section>
  );
}

function SetupNotice({ title, body, compact = false, loading = false, tone = 'neutral', role }) {
  return (
    <div
      className={`bl-setup-notice is-${tone}${compact ? ' is-compact' : ''}`}
      role={loading ? 'status' : role}
      aria-live={loading ? 'polite' : undefined}
      aria-busy={loading ? 'true' : undefined}
    >
      {loading && <Beacon size={compact ? 'control' : 'page'} delayMs={0} />}
      <strong>{title}</strong>
      <span>{body}</span>
    </div>
  );
}

function CreativePreviewDialog({ preview, onClose, onMediaError }) {
  const closeButtonRef = usePreviewCloseFocus(preview.status);
  const containFocus = (event) => {
    if (event.key !== 'Tab') return;
    const controls = [...event.currentTarget.querySelectorAll('button:not(:disabled), video[controls], [tabindex]:not([tabindex="-1"])')]
      .filter((element) => element.getClientRects().length > 0);
    if (!controls.length) return;
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const entry = preview.entry;
  return (
    <div className="bl-preview-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="bl-preview-dialog" role="dialog" aria-modal="true" aria-labelledby="setup-preview-title" data-preview-mode="details" onKeyDown={containFocus}>
        <div className="bl-preview-heading">
          <div><div className="bl-kicker">Published creative</div><h2 id="setup-preview-title">{entry.package?.displayName || entry.version.headline}</h2></div>
          <button ref={closeButtonRef} type="button" aria-label="Close creative preview" onClick={onClose}>×</button>
        </div>
        {preview.status === 'loading' && <SetupNotice compact loading title="Loading private preview" body="Preparing a short-lived video link." />}
        {preview.status === 'unavailable' && <SetupNotice compact role="status" title="Video preview unavailable" body="The exact approved copy remains available below." />}
        {preview.status === 'ready' && <ProtectedVideo className="bl-preview-video" controls preload="metadata" poster={preview.posterDescriptor?.url || undefined} src={preview.descriptor?.url} onError={() => onMediaError?.(preview)}>Your browser does not support video playback.</ProtectedVideo>}
        <div className="bl-preview-copy">
          <div><span>Headline</span><strong>{entry.version.headline}</strong></div>
          <div><span>Primary text</span><p>{entry.version.primaryText}</p></div>
          <div><span>Call to action</span><strong>{String(entry.version.callToAction || 'LEARN_MORE').replaceAll('_', ' ')}</strong></div>
        </div>
      </section>
    </div>
  );
}
